import { de, enUS, es } from 'date-fns/locale';
import {
  AlertCircle,
  Bike,
  Calendar,
  CheckCircle,
  Clock,
  CreditCard,
  DollarSign,
  Globe,
  List,
  Loader,
  MapPin,
  Minus,
  Package,
  Plus,
  Receipt,
  Save,
  User,
  X
} from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import DateRangeFilter from '../components/DateRangeFilter.jsx';
import SingleDatePicker from '../components/SingleDatePicker.jsx';
import MapaEntrega from '../components/MapaEntrega.jsx';
import ResumenReserva from '../components/ResumenReserva.jsx';
import API_BASE_URL, { API_ENDPOINTS, buildApiUrl } from '../config/api.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { splitLineAmountsIntoBaseAndVat } from '../utils/pricingVat.js';
import { useTranslations } from '../utils/translations.js';
import {
  calculateDynamicPrice,
  getAveragePricePerDay,
  normalizeVehicleModelPricing
} from '../utils/vehiclePricing.js';
import './CrearReserva.css';

/** HH:mm libre (hora y minutos) para carrito: solo acotación 0–23 / 0–59, sin horario comercial. */
function parseCartTimeParts(hm) {
  const parts = String(hm ?? '09:00').trim().split(':');
  const rawH = parseInt(parts[0], 10);
  const rawM = parseInt(parts[1], 10);
  const h = Number.isFinite(rawH) ? Math.min(23, Math.max(0, rawH)) : 9;
  const m = Number.isFinite(rawM) ? Math.min(59, Math.max(0, rawM)) : 0;
  return { h, m };
}

/** Mínimo de total de reserva (€) para poder usar pago parcial (alineado con backend). */
const MIN_PARTIAL_TOTAL_EUR = 100;

function tryShowNativePicker(input) {
  if (!input || typeof input.showPicker !== 'function') return;
  try {
    input.showPicker();
  } catch {
    /* Sin gesto de usuario o tipo no soportado */
  }
}

const CrearReserva = ({ onClose, onSuccess, onBookingCreated, prefillCustomer = null }) => {
  const { user, authenticatedFetch } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reservationCreated, setReservationCreated] = useState(false);
  const [language, setLanguage] = useState('es');
  const t = useTranslations(language);
  const datePickerLocale = useMemo(
    () => (language === 'en' ? enUS : language === 'de' ? de : es),
    [language]
  );
  
  // Función para obtener la fecha actual en formato date (YYYY-MM-DD)
  const getCurrentDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Función para obtener la fecha de mañana en formato date (YYYY-MM-DD)
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const year = tomorrow.getFullYear();
    const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const day = String(tomorrow.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Función para obtener la fecha de un día después de una fecha dada
  const getNextDayDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    date.setDate(date.getDate() + 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Datos del formulario
  const [formData, setFormData] = useState({
    // Datos del cliente
    customerName: '',
    customerEmail: '',
    customerPhonePrefix: '',
    customerPhone: '',
    customerDni: '',
    customerAddress: '', // Obligatorio si total > 2.999,99 € (factura ordinaria)
    
    // Datos de la reserva (solo fechas)
    startDate: getCurrentDate(),
    endDate: getTomorrowDate(),
    // Horas (solo para CARRITOS)
    startTime: '09:00',
    endTime: '21:00',
    /** Duración del alquiler de carrito en horas (1–4); el fin se calcula desde inicio + horas */
    cartRentalHours: 2,
    deliveryType: 'PICKUP',
    deliveryAddress: '',
    deliveryDistance: null,
    deliveryCost: null,
    
    // Vehículos y accesorios
    selectedVehicles: [],
    selectedAccessories: {},
    
    // Pago
    paymentMethod: '',
    paymentStatus: 'PAID',
    total: 0,
    agencyId: null,
    /** Referencia/ticket de colaborador (solo si paymentMethod === COLABORADOR) */
    ticket: '',

    /** FULL | PARTIAL — solo reservas BIKES sin colaborador */
    paymentMode: 'FULL',
    /** Entero 1–100 opcional; vacío = usar % de Ajustes */
    partialDepositPercentOverride: '',

    // Notas internas de la reserva (solo admin)
    notes: '',

    /** Paso 5: % de descuento manual opcional (0–100) sobre el subtotal con IVA */
    manualDiscountPercent: '',

    /** Medida opcional de entrepierna (cm) por vehículo seleccionado (solo modelos que lo permiten) */
    inseamCmByVehicleId: {}
  });

  // Estado para el checkbox de políticas de cancelación
  const [acceptedCancellationPolicy, setAcceptedCancellationPolicy] = useState(false);

  // Estados para datos externos
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [accessoriesByModel, setAccessoriesByModel] = useState({});
  const [loadingAccessories, setLoadingAccessories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedBusinessGroup, setSelectedBusinessGroup] = useState('BIKES');
  const [agencies, setAgencies] = useState([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState('');
  /** % de anticipo desde Ajustes (fallback 20) — para preview en tiempo real */
  const [settingsPartialPercent, setSettingsPartialPercent] = useState(20);

  // Misma convención que bike-rental-booking (bicis: día 09:00 → día 21:00 local → ISO).
  // Carritos: el fin se calcula siempre como inicio + N h (no usar endDate/endTime del estado: pueden ir un render por detrás al cambiar la hora).
  const getCartRentalRangeIso = (startDateStr) => {
    if (!startDateStr || selectedBusinessGroup !== 'CARRITOS') return null;
    const h = Math.min(4, Math.max(1, parseInt(String(formData.cartRentalHours), 10) || 2));
    const { h: sh, m: sm } = parseCartTimeParts(formData.startTime);
    const start = new Date(`${startDateStr}T${String(sh).padStart(2, '0')}:${String(sm).padStart(2, '0')}:00`);
    if (Number.isNaN(start.getTime())) return null;
    const end = new Date(start.getTime() + h * 60 * 60 * 1000);
    return { startISO: start.toISOString(), endISO: end.toISOString() };
  };

  const dateToStartDateTime = (dateString) => {
    if (!dateString) return null;
    if (selectedBusinessGroup === 'CARRITOS') {
      const r = getCartRentalRangeIso(dateString);
      return r ? r.startISO : null;
    }
    const d = new Date(`${dateString}T09:00:00`);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  };

  const dateToEndDateTime = (dateString) => {
    if (selectedBusinessGroup === 'CARRITOS') {
      const r = getCartRentalRangeIso(formData.startDate);
      return r ? r.endISO : null;
    }
    if (!dateString) return null;
    const d = new Date(`${dateString}T21:00:00`);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  };

  // Si el usuario es AGENCY, inicializar con su agencia
  useEffect(() => {
    if (user?.role === 'AGENCY' && user?.agencyCode) {
      // Buscar la agencia del usuario
      fetchAgencies().then(() => {
        const userAgency = agencies.find(a => a.code === user.agencyCode);
        if (userAgency) {
          setSelectedAgencyId(userAgency.id.toString());
          setFormData(prev => ({
            ...prev,
            paymentMethod: 'COLABORADOR',
            agencyId: userAgency.id
          }));
        }
      });
    }
  }, [user, agencies.length]);

  // Al cambiar el tipo de vehículo, limpiar selección incompatible (layout: antes del fetch de disponibilidad).
  useLayoutEffect(() => {
    setSelectedCategory('');
    setSelectedSize('');
    setFormData((prev) => ({
      ...prev,
      selectedVehicles: [],
      selectedAccessories: selectedBusinessGroup === 'CARRITOS' ? {} : prev.selectedAccessories,
      ...(selectedBusinessGroup === 'CARRITOS'
        ? {
            endDate: prev.startDate,
            cartRentalHours: Math.min(4, Math.max(1, parseInt(String(prev.cartRentalHours), 10) || 2)),
            deliveryType: 'PICKUP',
            deliveryAddress: '',
            deliveryDistance: null,
            deliveryCost: null,
          }
        : {}),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusinessGroup]);

  // Carritos: fin = inicio + N horas (layout para no llamar a la API con endTime 21:00 residual de bicis).
  useLayoutEffect(() => {
    if (selectedBusinessGroup !== 'CARRITOS') return;
    setFormData((prev) => {
      const h = Math.min(4, Math.max(1, parseInt(String(prev.cartRentalHours), 10) || 2));
      const { h: sh, m: sm } = parseCartTimeParts(prev.startTime);
      const start = new Date(`${prev.startDate}T${String(sh).padStart(2, '0')}:${String(sm).padStart(2, '0')}:00`);
      if (Number.isNaN(start.getTime()) || !prev.startDate) return prev;
      const end = new Date(start.getTime() + h * 60 * 60 * 1000);
      const pad = (n) => String(n).padStart(2, '0');
      const endDateStr = `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`;
      const endTimeStr = `${pad(end.getHours())}:${pad(end.getMinutes())}`;
      if (
        prev.endDate === endDateStr &&
        prev.endTime === endTimeStr &&
        Number(prev.cartRentalHours) === h
      ) {
        return prev;
      }
      return {
        ...prev,
        endDate: endDateStr,
        endTime: endTimeStr,
        cartRentalHours: h,
      };
    });
  }, [selectedBusinessGroup, formData.startDate, formData.startTime, formData.cartRentalHours]);

  useEffect(() => {
    setError(null);
  }, [selectedBusinessGroup]);

  /** Carritos no tienen paso 3 (accesorios); si el flujo queda en 3, saltar al 4. */
  useEffect(() => {
    if (selectedBusinessGroup === 'CARRITOS' && step === 3) {
      setStep(4);
    }
  }, [selectedBusinessGroup, step]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authenticatedFetch(buildApiUrl(`${API_ENDPOINTS.SETTINGS}/bike-partial-deposit`));
        const data = await res.json();
        if (!cancelled && data.success && data.data?.bikePartialDepositPercent != null) {
          setSettingsPartialPercent(data.data.bikePartialDepositPercent);
        }
      } catch {
        /* silencioso: preview con fallback */
      }
    })();
    return () => { cancelled = true; };
  }, [authenticatedFetch]);

  /** Misma lógica que el backend: anticipo en € enteros */
  const computeInitialDepositEuros = (totalEur, percent) => {
    const t = Number(totalEur);
    const p = Number(percent);
    if (!Number.isFinite(t) || t <= 0 || !Number.isFinite(p) || p <= 0) return 0;
    const rounded = Math.round((t * p) / 100);
    return Math.max(1, Math.min(rounded, Math.floor(t)));
  };

  // Estados para validación en tiempo real
  const [validationErrors, setValidationErrors] = useState({});

  /**
   * Desde «Nueva reserva con este cliente»: solo nombre, email, teléfono, DNI y dirección fiscal si existen.
   * Fechas, entrega, bicis, accesorios y pago quedan como una reserva nueva.
   */
  useLayoutEffect(() => {
    if (!prefillCustomer) return;
    const rawPrefix = prefillCustomer.customerPhonePrefix?.trim();
    const normalizedPrefix = rawPrefix
      ? (rawPrefix.startsWith('+') ? rawPrefix : `+${rawPrefix.replace(/^\+?/, '')}`)
      : '+34';
    setFormData((prev) => ({
      ...prev,
      customerName: prefillCustomer.customerName || '',
      customerEmail: prefillCustomer.customerEmail || '',
      customerPhonePrefix: normalizedPrefix,
      customerPhone: prefillCustomer.customerPhone || '',
      customerDni: prefillCustomer.customerDni || '',
      customerAddress: prefillCustomer.customerAddress || '',
      ...getDefaultDates(),
      deliveryType: 'PICKUP',
      deliveryAddress: '',
      deliveryDistance: null,
      deliveryCost: null,
      selectedVehicles: [],
      selectedAccessories: {},
      notes: '',
      paymentStatus: 'PAID',
      total: 0,
      ticket: '',
      paymentMode: 'FULL',
      partialDepositPercentOverride: '',
      manualDiscountPercent: '',
      ...(user?.role === 'AGENCY'
        ? {}
        : { paymentMethod: '', agencyId: null }),
    }));
    setSelectedCategory('');
    setSelectedSize('');
    setStep(1);
    setReservationCreated(false);
    setAcceptedCancellationPolicy(false);
    setValidationErrors({});
    setError(null);
  }, [prefillCustomer, user?.role]);

  // Validar campo específico
  // startDateOverride permite pasar valores actualizados para validación
  // Días cerrados (gestionados por ADMIN en /dias-cerrados)
  const [closedDays, setClosedDays] = useState([]);
  const closedDayKeys = useMemo(() => new Set(closedDays), [closedDays]);

  /** Fin automático al mover el inicio: día siguiente, o el mismo día si el siguiente está cerrado. */
  const getAutoEndDate = (startDate) => {
    const next = getNextDayDate(startDate);
    return closedDayKeys.has(next) ? startDate : next;
  };

  /** Fechas por defecto: inicio hoy (o el primer día abierto) y fin automático según getAutoEndDate. */
  const getDefaultDates = () => {
    let startDate = getCurrentDate();
    for (let i = 0; i < 366 && closedDayKeys.has(startDate); i += 1) {
      startDate = getNextDayDate(startDate);
    }
    return { startDate, endDate: getAutoEndDate(startDate) };
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authenticatedFetch(
          buildApiUrl(`${API_ENDPOINTS.CLOSED_DAYS}?from=${getCurrentDate()}`)
        );
        const data = await res.json();
        if (!cancelled && data.success) setClosedDays((data.data || []).map((d) => d.date));
      } catch (e) {
        if (!cancelled) console.error('Error cargando días cerrados:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [authenticatedFetch]);

  const validateField = (field, value, startDateOverride = null) => {
    setValidationErrors(prev => {
      const errors = { ...prev };
      
      // Usar valores override si están disponibles, sino usar formData
      // Si endDateOverride es una cadena no vacía, usarla; si es null o undefined, usar formData
      const currentStartDate = (startDateOverride !== null && startDateOverride !== undefined) ? startDateOverride : formData.startDate;
    
    switch (field) {
      case 'customerName': {
        const nameTrimmed = value?.trim() || '';
        if (!nameTrimmed) {
          errors.customerName = t.nameRequired;
        } else if (nameTrimmed.length < 2) {
          errors.customerName = t.nameMinLength;
        } else if (nameTrimmed.length > 100) {
          errors.customerName = t.nameMaxLength;
        } else if (!/^[\p{L}\s'-]+$/u.test(nameTrimmed)) {
          errors.customerName = t.nameInvalid;
        } else {
          delete errors.customerName;
        }
        break;
      }
      
      case 'customerEmail':
        if (!value?.trim()) {
          errors.customerEmail = t.emailRequired;
        } else if (!isValidEmail(value)) {
          errors.customerEmail = t.emailInvalid;
        } else {
          delete errors.customerEmail;
        }
        break;
      
      case 'customerPhonePrefix':
        if (!value?.trim()) {
          errors.customerPhonePrefix = t.phonePrefixRequired;
        } else if (!/^\+?[1-9]\d{0,3}$/.test(value.trim())) {
          errors.customerPhonePrefix = t.phonePrefixInvalid;
        } else {
          delete errors.customerPhonePrefix;
        }
        break;
      
      case 'customerPhone':
        if (!value?.trim()) {
          errors.customerPhone = t.phoneRequired;
        } else {
          delete errors.customerPhone;
        }
        break;
      
      case 'customerDni':
        // Documento de identificación obligatorio: DNI (8 dígitos + letra), NIE (X/Y/Z + 7 dígitos + letra) o pasaporte (alfanumérico 5-20 caracteres)
        { const docTrimmed = value?.trim().toUpperCase() || '';
        if (!docTrimmed) {
          errors.customerDni = t.idDocumentRequired;
        } else {
          const dniRegex = /^[0-9]{8}[A-Z]$/;
          const nieRegex = /^[XYZ][0-9]{7}[A-Z]$/;
          const passportRegex = /^[A-Z0-9]{5,20}$/;
          if (dniRegex.test(docTrimmed) || nieRegex.test(docTrimmed) || passportRegex.test(docTrimmed)) {
            delete errors.customerDni;
          } else {
            errors.customerDni = t.idDocumentInvalid;
          }
        }
        break; }
      
      case 'customerAddress': {
        const tot = calculateTotal();
        if (tot > 2999.99 && !(value?.trim())) {
          errors.customerAddress = t.addressRequiredWhenTotal;
        } else {
          delete errors.customerAddress;
        }
        break; }
      
      case 'startDate':
        if (!value) {
          errors.startDate = t.startDateRequired;
        } else {
          const startDate = new Date(value);
          startDate.setHours(0, 0, 0, 0);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          if (startDate < today) {
            errors.startDate = t.startDatePast;
          } else if (closedDayKeys.has(value)) {
            errors.startDate = t.dateClosed;
          } else {
            // Eliminar el error cuando la fecha es válida
            // No validamos comparación con endDate porque el sistema ajusta automáticamente
            // la fecha de fin cuando startDate > endDate
            delete errors.startDate;
          }
        }
        break;
      
      case 'endDate':
        if (!value) {
          errors.endDate = t.endDateRequired;
        } else {
          const endDate = new Date(value);
          endDate.setHours(0, 0, 0, 0);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          // Permitir que endDate sea igual a hoy (puede reservar para hoy)
          if (endDate < today) {
            errors.endDate = t.endDateFuture;
          } else if (closedDayKeys.has(value)) {
            errors.endDate = t.dateClosed;
          } else if (currentStartDate) {
            const startDate = new Date(currentStartDate);
            startDate.setHours(0, 0, 0, 0);
            // Permitir que endDate sea igual a startDate (mismo día = 1 día de reserva)
            // Solo error si endDate es menor que startDate
            if (endDate < startDate) {
              errors.endDate = t.endDateBeforeStart;
            } else {
              delete errors.endDate;
            }
          } else {
            delete errors.endDate;
          }
        }
        break;
    }
    
    return errors;
    });
  };

  // Manejar cambio de campo con validación
  const handleFieldChange = (field, value) => {
    let adjustedEndDate = null;
    // El '00' de marcación internacional equivale a '+' (ej. 0034 -> +34)
    const adjustedValue = field === 'customerPhonePrefix' && typeof value === 'string' && value.trimStart().startsWith('00')
      ? `+${value.trimStart().slice(2)}`
      : value;

    setFormData(prev => {
      const newData = { ...prev, [field]: adjustedValue };
      
      // Si se modifica la fecha de inicio y es posterior a la fecha de fin,
      // ajustar automáticamente la fecha de fin a un día más que la fecha de inicio
      if (field === 'startDate' && value) {
        if (prev.endDate) {
          const startDate = new Date(value);
          startDate.setHours(0, 0, 0, 0);
          const endDate = new Date(prev.endDate);
          endDate.setHours(0, 0, 0, 0);
          
          // Si la fecha de inicio es posterior a la fecha de fin, ajustar la fecha de fin
          if (startDate > endDate) {
            adjustedEndDate = getAutoEndDate(value);
            newData.endDate = adjustedEndDate;
          }
        }
      }
      
      return newData;
    });
    
    // Validar después de actualizar el estado
    if (adjustedEndDate) {
      // Cuando se ajusta automáticamente la fecha de fin, las fechas siempre serán válidas
      // (adjustedEndDate es siempre un día después de la fecha de inicio)
      // Por lo tanto, NO validamos la comparación de fechas, solo validamos fechas pasadas
      // Eliminamos inmediatamente cualquier error de comparación de fechas
      setValidationErrors(prevErrors => {
        const updatedErrors = { ...prevErrors };
        
        // Eliminar explícitamente el error de "fecha de inicio después de fin"
        // porque sabemos que las fechas son válidas (adjustedEndDate es siempre un día después)
        delete updatedErrors.startDate;
        delete updatedErrors.endDate;
        
        // Solo validar que las fechas no sean pasadas
        if (value) {
          const startDate = new Date(value);
          startDate.setHours(0, 0, 0, 0);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          if (startDate < today) {
            updatedErrors.startDate = t.startDatePast;
          } else if (closedDayKeys.has(value)) {
            updatedErrors.startDate = t.dateClosed;
          }
        }
        
        if (adjustedEndDate) {
          const endDate = new Date(adjustedEndDate);
          endDate.setHours(0, 0, 0, 0);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          if (endDate < today) {
            updatedErrors.endDate = t.endDateFuture;
          } else if (closedDayKeys.has(adjustedEndDate)) {
            updatedErrors.endDate = t.dateClosed;
          }
        }
        
        return updatedErrors;
      });
    } else if (field === 'startDate') {
      // Validar startDate (no validamos comparación con endDate porque el sistema
      // ajusta automáticamente la fecha de fin cuando es necesario)
      validateField(field, value);
    } else {
      validateField(field, value);
    }
  };

  /** Rango de fechas de la reserva (mismo criterio que paso 1: 09:00–21:00 vía dateToStartDateTime). */
  const handleBookingDateRangeChange = (from, to) => {
    if (!from || !to) return;

    if (selectedBusinessGroup === 'CARRITOS') {
      setFormData((prev) => ({ ...prev, startDate: from, endDate: from }));
      validateField('startDate', from);
      validateField('endDate', from, from);
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startD = new Date(from);
    startD.setHours(0, 0, 0, 0);
    if (startD < today) return;

    let startDate = from;
    let endDate = to;
    const sd = new Date(startDate);
    sd.setHours(0, 0, 0, 0);
    const ed = new Date(endDate);
    ed.setHours(0, 0, 0, 0);
    if (sd > ed) {
      endDate = getAutoEndDate(startDate);
    }

    setFormData(prev => ({ ...prev, startDate, endDate }));
    validateField('startDate', startDate);
    validateField('endDate', endDate, startDate);
  };

  // Cargar categorías
  const fetchCategories = async () => {
    try {
      const response = await authenticatedFetch(buildApiUrl(API_ENDPOINTS.VEHICLE_CATEGORIES));
      if (!response.ok) {
        console.error('Error al cargar categorías:', response.status, response.statusText);
        setCategories([]);
        return;
      }
      const data = await response.json();
      console.log('Categorías recibidas:', data);
      // El backend devuelve directamente un array o un objeto con success/data
      if (Array.isArray(data)) {
        setCategories(data);
      } else if (data.success && Array.isArray(data.data)) {
        setCategories(data.data);
      } else {
        console.warn('Formato de respuesta de categorías inesperado:', data);
        setCategories([]);
      }
    } catch (error) {
      console.error('Error al cargar categorías:', error);
      setCategories([]);
    }
  };

  // Cargar accesorios (todos para referencia)
  const fetchAccessories = async () => {
    try {
      const response = await authenticatedFetch(buildApiUrl(API_ENDPOINTS.ACCESSORIES));
      const data = await response.json();
      if (Array.isArray(data)) {
        setAccessories(data);
      } else if (data.success && Array.isArray(data.data)) {
        setAccessories(data.data);
      }
    } catch (error) {
      console.error('Error al cargar accesorios:', error);
    }
  };

  // Cargar accesorios por modelo de vehículo
  const fetchAccessoriesByModel = async (modelId) => {
    try {
      setLoadingAccessories(prev => ({ ...prev, [modelId]: true }));
      const response = await authenticatedFetch(
        `${buildApiUrl(API_ENDPOINTS.ACCESSORIES)}/model/${modelId}?lang=${language}`
      );
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      const accessoriesData = await response.json();
      setAccessoriesByModel(prev => ({
        ...prev,
        [modelId]: Array.isArray(accessoriesData) ? accessoriesData : []
      }));
    } catch (error) {
      console.error(`Error al cargar accesorios para modelo ${modelId}:`, error);
      setAccessoriesByModel(prev => ({
        ...prev,
        [modelId]: []
      }));
    } finally {
      setLoadingAccessories(prev => ({ ...prev, [modelId]: false }));
    }
  };

  // Cargar accesorios cuando cambian los vehículos seleccionados
  useEffect(() => {
    if (selectedBusinessGroup === 'CARRITOS') return;
    if (formData.selectedVehicles.length > 0) {
      formData.selectedVehicles.forEach(selectedVehicle => {
        if (selectedVehicle.modelId && !accessoriesByModel[selectedVehicle.modelId]) {
          fetchAccessoriesByModel(selectedVehicle.modelId);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.selectedVehicles, selectedBusinessGroup]);

  // Cargar vehículos disponibles
  const fetchAvailableVehicles = async () => {
    if (!formData.startDate || !formData.endDate) return;

    try {
      setError(null);
      setLoading(true);
      // Convertir fechas a datetime para el backend
      const startDateTime = dateToStartDateTime(formData.startDate);
      const endDateTime = dateToEndDateTime(formData.endDate);
      
      const params = new URLSearchParams({
        startDateTime: startDateTime,
        endDateTime: endDateTime,
        businessGroup: selectedBusinessGroup,
        ...(selectedCategory && { categoryId: selectedCategory })
      });

      const response = await authenticatedFetch(`${buildApiUrl(API_ENDPOINTS.BOOKINGS)}/available-vehicles?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setAvailableVehicles(data.data);
      } else {
        setError(data.message);
      }
    } catch (error) {
      setError(t.errorLoadingVehicles);
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Cargar agencias
  const fetchAgencies = async () => {
    try {
      const response = await authenticatedFetch(buildApiUrl(API_ENDPOINTS.AGENCIES));
      if (!response.ok) {
        console.error('Error al cargar agencias:', response.status, response.statusText);
        setAgencies([]);
        return;
      }
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setAgencies(data.data);
      } else if (Array.isArray(data)) {
        setAgencies(data);
      } else {
        console.warn('Formato de respuesta de agencias inesperado:', data);
        setAgencies([]);
      }
    } catch (error) {
      console.error('Error al cargar agencias:', error);
      setAgencies([]);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchAccessories();
    fetchAgencies();
  }, []);

  // Si el usuario es AGENCY, configurar automáticamente método de pago y agencia
  useEffect(() => {
    if (user?.role === 'AGENCY' && user?.agencyCode && agencies.length > 0) {
      const userAgency = agencies.find(a => a.code === user.agencyCode);
      if (userAgency) {
        setSelectedAgencyId(userAgency.id.toString());
        setFormData(prev => ({
          ...prev,
          paymentMethod: 'COLABORADOR',
          agencyId: userAgency.id
        }));
      }
    }
  }, [user, agencies, user?.agencyCode]);

  // Validar fechas iniciales cuando se carga el componente (y al recibir los días cerrados).
  // Si el formulario sigue con las fechas por defecto (hoy → mañana), recalcularlas evitando días cerrados.
  useEffect(() => {
    let { startDate, endDate } = formData;
    if (startDate === getCurrentDate() && endDate === getTomorrowDate()) {
      ({ startDate, endDate } = getDefaultDates());
      if (startDate !== formData.startDate || endDate !== formData.endDate) {
        setFormData((prev) => ({ ...prev, startDate, endDate }));
      }
    }
    validateField('startDate', startDate);
    validateField('endDate', endDate, startDate);
  }, [closedDayKeys]);

  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      fetchAvailableVehicles();
    }
  }, [
    formData.startDate,
    formData.endDate,
    formData.startTime,
    formData.endTime,
    formData.cartRentalHours,
    selectedCategory,
    selectedBusinessGroup,
  ]);

  // Al cambiar el grupo, restablecer categoría para evitar combinación inválida
  useEffect(() => {
    const currentCategory = categories.find(c => c.id === parseInt(selectedCategory));
    if (selectedCategory && currentCategory && currentCategory.businessGroup && currentCategory.businessGroup !== selectedBusinessGroup) {
      setSelectedCategory('');
    }
  }, [selectedBusinessGroup, categories]);

  // Alineado con bike-rental-booking/src/services/pricingService.js (calculateDurationInDays)
  // y con las mismas anclas horarias que DateSelection (09:00 inicio, 21:00 fin).
  const calculateDuration = () => {
    if (!formData.startDate || !formData.endDate) return 0;
    const start = new Date(`${formData.startDate}T09:00:00`);
    const end = new Date(`${formData.endDate}T21:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(1, diffDays);
  };

  /** Días (bicis) u horas de carrito para multiplicar accesorios DAILY (carritos no llevan accesorios). */
  const getAccessoryDurationUnits = () => {
    if (selectedBusinessGroup === 'CARRITOS') {
      return Math.min(4, Math.max(1, parseInt(String(formData.cartRentalHours), 10) || 2));
    }
    return calculateDuration();
  };

  // Función para formatear la duración de manera más descriptiva
  const formatDuration = () => {
    if (!formData.startDate || !formData.endDate) return '';

    if (selectedBusinessGroup === 'CARRITOS') {
      const h = Math.min(4, Math.max(1, parseInt(String(formData.cartRentalHours), 10) || 2));
      return h === 1 ? '1 hora' : `${h} horas`;
    }

    const days = calculateDuration();

    if (days === 1) {
      return '1 día';
    } else {
      return `${days} días`;
    }
  };

  // Calcular precio de un vehículo (total del backend con temporadas + descuentos, o misma lógica que booking)
  const calculateVehiclePrice = (vehicle, durationInDays) => {
    if (!vehicle) return 0;
    if (selectedBusinessGroup === 'CARRITOS') {
      const h = Math.min(4, Math.max(1, parseInt(String(formData.cartRentalHours), 10) || 2));
      const key = `cartPrice${h}h`;
      const fromField = Number(vehicle[key]);
      if (Number.isFinite(fromField) && fromField >= 0) {
        return Math.round(fromField * 100) / 100;
      }
      const totalFromApi = Number(vehicle.totalPrice);
      if (Number.isFinite(totalFromApi) && totalFromApi > 0) {
        return Math.round(totalFromApi * 100) / 100;
      }
      return 0;
    }
    if (!durationInDays || durationInDays < 1) {
      return 0;
    }
    const totalFromApi = Number(vehicle.totalPrice);
    if (Number.isFinite(totalFromApi) && totalFromApi > 0) {
      return Math.round(totalFromApi * 100) / 100;
    }
    const pricing = normalizeVehicleModelPricing(vehicle);
    if (!pricing) {
      return 0;
    }
    return calculateDynamicPrice(pricing, durationInDays);
  };

  // Precio medio por día para el paso 2 (alineado con bike-rental-booking: total/día o regla 3–5)
  const getPricePerDay = (vehicle, durationInDays) => {
    if (selectedBusinessGroup === 'CARRITOS') {
      return calculateVehiclePrice(vehicle, durationInDays);
    }
    if (!vehicle || !durationInDays || durationInDays < 1) {
      return 0;
    }
    const totalFromApi = Number(vehicle.totalPrice);
    if (Number.isFinite(totalFromApi) && totalFromApi > 0) {
      return Math.round((totalFromApi / durationInDays) * 100) / 100;
    }
    const pricing = normalizeVehicleModelPricing(vehicle);
    if (!pricing) {
      return 0;
    }
    return getAveragePricePerDay(pricing, durationInDays);
  };


  // Calcular precio total
  const calculateTotal = () => {
    const durationInDays = calculateDuration();
    let total = 0;

    // Sumar precios de vehículos
    formData.selectedVehicles.forEach(selectedVehicle => {
      const vehicle = availableVehicles.find(v => v.id === selectedVehicle.modelId);
      if (vehicle) {
        const vehiclePrice = calculateVehiclePrice(vehicle, durationInDays);
        total += vehiclePrice;
      }
    });

    // Sumar precios de accesorios
    Object.values(formData.selectedAccessories).forEach(accessoryIds => {
      accessoryIds.forEach(accessoryId => {
        const accessory = accessories.find(a => a.id === accessoryId);
        if (accessory) {
          const isDaily = accessory.accessoryPriceType === 'DAILY';
          const price = isDaily ? accessory.price * getAccessoryDurationUnits() : accessory.price;
          total += price;
        }
      });
    });

    // Sumar coste de entrega si es a domicilio
    if (formData.deliveryType === 'DELIVERY' && formData.deliveryCost) {
      total += formData.deliveryCost;
    }

    return total;
  };

  // Obtener cantidad seleccionada por modelo y talla (carritos: sin talla, todo el modelo)
  const getSelectedCount = (modelId, size) => {
    if (selectedBusinessGroup === 'CARRITOS') {
      return formData.selectedVehicles.filter((v) => v.modelId === modelId).length;
    }
    return formData.selectedVehicles.filter(
      (v) => v.modelId === modelId && v.size === size
    ).length;
  };

  // Manejar selección de vehículo por modelo y talla
  const handleVehicleSelection = (modelId, size, action) => {
    setFormData((prev) => {
      const newSelection = [...prev.selectedVehicles];
      const model = availableVehicles.find((m) => m.id === modelId);

      if (!model) return prev;

      const isCart = selectedBusinessGroup === 'CARRITOS';
      const availableVehiclesBySize = isCart
        ? model.vehicles || []
        : model.vehicles.filter((v) => v.size === size);

      if (action === 'add') {
        const selectedVehicleIds = newSelection.map((v) => v.vehicleId);
        const availableVehicle = availableVehiclesBySize.find(
          (v) => !selectedVehicleIds.includes(v.id)
        );

        if (availableVehicle) {
          newSelection.push({
            modelId,
            vehicleId: availableVehicle.id,
            modelName: model.name,
            size: availableVehicle.size,
            serialNumber: availableVehicle.serialNumber,
          });
        }
      } else if (action === 'remove') {
        if (isCart) {
          const selectedForModel = newSelection
            .map((v, index) => ({ v, index }))
            .filter(({ v }) => v.modelId === modelId);
          if (selectedForModel.length > 0) {
            const lastIndex = selectedForModel[selectedForModel.length - 1].index;
            newSelection.splice(lastIndex, 1);
          }
        } else {
          const selectedForSize = newSelection
            .map((v, index) => ({ v, index }))
            .filter(({ v }) => v.modelId === modelId && v.size === size);

          if (selectedForSize.length > 0) {
            const lastIndex = selectedForSize[selectedForSize.length - 1].index;
            newSelection.splice(lastIndex, 1);
          }
        }
      }

      return { ...prev, selectedVehicles: newSelection };
    });
  };

  // Manejar selección de accesorio
  const handleAccessoryToggle = (accessoryId, vehicleId) => {
    if (selectedBusinessGroup === 'CARRITOS') return;
    setFormData(prev => {
      const newAccessories = { ...prev.selectedAccessories };
      
      if (!newAccessories[vehicleId]) {
        newAccessories[vehicleId] = [];
      }
      
      const isSelected = newAccessories[vehicleId].includes(accessoryId);
      const selectedVehicle = prev.selectedVehicles.find(v => v.vehicleId === vehicleId);
      const modelAccessories = selectedVehicle ? (accessoriesByModel[selectedVehicle.modelId] || []) : [];
      const accessory =
        modelAccessories.find(a => a.id === accessoryId) ||
        accessories.find(a => a.id === accessoryId);
      const isInsurance = accessory?.category === 'INSURANCE';
      
      if (isSelected) {
        newAccessories[vehicleId] = newAccessories[vehicleId].filter(id => id !== accessoryId);
      } else {
        if (isInsurance) {
          const insuranceIds = new Set(
            [
              ...modelAccessories.filter(a => a.category === 'INSURANCE').map(a => a.id),
              ...accessories.filter(a => a.category === 'INSURANCE').map(a => a.id)
            ].filter(Boolean)
          );
          newAccessories[vehicleId] = [
            ...newAccessories[vehicleId].filter(id => !insuranceIds.has(id)),
            accessoryId
          ];
        } else {
          newAccessories[vehicleId] = [...newAccessories[vehicleId], accessoryId];
        }
      }
      
      return { ...prev, selectedAccessories: newAccessories };
    });
  };

  // Calcular precio de accesorio
  const calculateAccessoryPrice = (accessory) => {
    const durationUnits = getAccessoryDurationUnits();
    if (accessory.accessoryPriceType === 'FIXED' || accessory.accessoryPriceType === 'DAILY') {
      return accessory.accessoryPriceType === 'DAILY' 
        ? accessory.price * durationUnits 
        : accessory.price;
    }
    return accessory.price;
  };

  // Obtener texto del precio del accesorio
  const getAccessoryPriceText = (accessory) => {
    const totalPrice = calculateAccessoryPrice(accessory);
    
    if (accessory.accessoryPriceType === 'FIXED') {
      return formatPrice(totalPrice);
    } else {
      return `${formatPrice(accessory.price)}${t.perDay} (${formatPrice(totalPrice)} ${t.total || 'total'})`;
    }
  };

  /**
   * Desglose por línea con IVA (mismo total que Quipu / resumen).
   * Los importes por concepto son los totales de línea con IVA incluido.
   */
  const getSplitPricing = () => {
    const durationInDays = calculateDuration();
    let vehiclesRaw = 0;
    let accessoriesOnlyTotal = 0;
    let insuranceTotal = 0;
    formData.selectedVehicles.forEach(vehicle => {
      const vehicleModel = availableVehicles.find(v => v.id === vehicle.modelId);
      if (vehicleModel) vehiclesRaw += calculateVehiclePrice(vehicleModel, durationInDays);
    });
    Object.entries(formData.selectedAccessories).forEach(([vehicleId, accessoryIds]) => {
      const selectedVehicle = formData.selectedVehicles.find(v => v.vehicleId === parseInt(vehicleId));
      if (selectedVehicle) {
        const modelAccessories = accessoriesByModel[selectedVehicle.modelId] || [];
        accessoryIds.forEach(accessoryId => {
          let accessory = modelAccessories.find(a => a.id === accessoryId);
          if (!accessory) accessory = accessories.find(a => a.id === accessoryId);
          if (accessory) {
            const price = calculateAccessoryPrice(accessory);
            if (accessory.category === 'INSURANCE') insuranceTotal += price;
            else accessoriesOnlyTotal += price;
          }
        });
      }
    });
    const deliveryRaw = (formData.deliveryType === 'DELIVERY' && formData.deliveryCost) ? formData.deliveryCost : 0;
    const rawTotal = Math.round((vehiclesRaw + accessoriesOnlyTotal + insuranceTotal + deliveryRaw) * 100) / 100;
    const lineAmounts = [
      ...(vehiclesRaw > 0 ? [{ concept: 'vehicles', amount: vehiclesRaw }] : []),
      ...(insuranceTotal > 0 ? [{ concept: 'insurance', amount: insuranceTotal }] : []),
      ...(accessoriesOnlyTotal > 0 ? [{ concept: 'accessories', amount: accessoriesOnlyTotal }] : []),
      ...(deliveryRaw > 0 ? [{ concept: 'delivery', amount: deliveryRaw }] : [])
    ];
    const linesWithVat = splitLineAmountsIntoBaseAndVat(lineAmounts, rawTotal);
    const getT = (c) => linesWithVat.find(l => l.concept === c)?.total ?? 0;
    const grandTotal = linesWithVat.reduce((s, l) => s + l.total, 0);
    return {
      grandTotal,
      vehiclesTotal: getT('vehicles'),
      accessoriesTotal: getT('accessories'),
      insuranceTotal: getT('insurance'),
      deliveryCost: getT('delivery'),
    };
  };

  const getGrandTotalFromSplit = () => getSplitPricing().grandTotal;

  /** Subtotal con IVA (antes del descuento manual del paso 5). */
  const getSubtotalBeforeManualDiscount = () =>
    formData.selectedVehicles?.length > 0 ? getGrandTotalFromSplit() : calculateTotal();

  const getParsedManualDiscountPercent = () => {
    const raw = formData.manualDiscountPercent;
    if (raw === '' || raw == null) return 0;
    const n = parseFloat(String(raw).replace(',', '.'));
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.min(100, n);
  };

  const getManualDiscountEuros = () => {
    const base = getSubtotalBeforeManualDiscount();
    const pct = getParsedManualDiscountPercent();
    return Math.round(base * (pct / 100) * 100) / 100;
  };

  const getTotalAfterManualDiscount = () => {
    const sub = getSubtotalBeforeManualDiscount();
    const disc = getManualDiscountEuros();
    return Math.round((sub - disc) * 100) / 100;
  };

  const totalForPartialRule = getTotalAfterManualDiscount();
  const partialPaymentAllowed = totalForPartialRule > MIN_PARTIAL_TOTAL_EUR;
  /** Anticipo + liquidación efectivo → el estado de pago se fuerza a PAID (aún no existe "pagado parcial") */
  const isPartialPaymentSelected =
    selectedBusinessGroup === 'BIKES' &&
    formData.paymentMethod !== 'COLABORADOR' &&
    partialPaymentAllowed &&
    formData.paymentMode === 'PARTIAL';

  useEffect(() => {
    if (step !== 5) return;
    const totalAfter = getTotalAfterManualDiscount();
    if (totalAfter <= MIN_PARTIAL_TOTAL_EUR && formData.paymentMode === 'PARTIAL') {
      setFormData(prev => ({ ...prev, paymentMode: 'FULL' }));
    }
    // formData cubre cambios de total y % descuento
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, formData]);

  // Crear la reserva
  const handleCreateBooking = async () => {
    try {
      setLoading(true);
      setError(null);

      // const durationInDays = calculateDuration();
      const split = formData.selectedVehicles?.length > 0 ? getSplitPricing() : null;
      const manualPct = getParsedManualDiscountPercent();
      const total = getTotalAfterManualDiscount();

      // Preparar datos de vehículos
      const vehicles = formData.selectedVehicles.map(v => v.vehicleId);

      const inseamCmByVehicleId = formData.inseamCmByVehicleId || {};

      // Preparar datos de accesorios
      const accessoriesData = [];
      Object.entries(formData.selectedAccessories).forEach(([vehicleId, accessoryIds]) => {
        // Encontrar el vehículo para obtener su modelId
        const selectedVehicle = formData.selectedVehicles.find(v => v.vehicleId === parseInt(vehicleId));
        if (!selectedVehicle) return;
        
        // Obtener accesorios del modelo correspondiente
        const modelAccessories = accessoriesByModel[selectedVehicle.modelId] || [];
        
        accessoryIds.forEach(accessoryId => {
          // Buscar primero en los accesorios del modelo, luego en la lista general
          let accessory = modelAccessories.find(a => a.id === accessoryId);
          if (!accessory) {
            accessory = accessories.find(a => a.id === accessoryId);
          }
          
          if (accessory) {
            const isDaily = accessory.accessoryPriceType === 'DAILY';
            const price = isDaily ? accessory.price * getAccessoryDurationUnits() : accessory.price;
            accessoriesData.push({
              vehicleId: parseInt(vehicleId),
              accessoryId,
              price
            });
          }
        });
      });

      // Convertir fechas a datetime para el backend
      const startDateTime = dateToStartDateTime(formData.startDate);
      const endDateTime = dateToEndDateTime(formData.endDate);

      const bookingData = {
        customerName: formData.customerName,
        customerEmail: formData.customerEmail,
        customerPhonePrefix: normalizePhonePrefix(formData.customerPhonePrefix),
        customerPhone: formData.customerPhone,
        customerDni: formData.customerDni?.trim().toUpperCase() || undefined,
        customerAddress: (total > 2999.99 && formData.customerAddress?.trim()) ? formData.customerAddress.trim() : undefined,
        startDateTime: startDateTime,
        endDateTime: endDateTime,
        deliveryType: formData.deliveryType,
        hotelAddress: formData.deliveryAddress || '', // Backend usa hotelAddress
        vehicles,
        inseamCmByVehicleId,
        accessories: selectedBusinessGroup === 'CARRITOS' ? [] : accessoriesData,
        total,
        ...(split
          ? {
              vehiclesTotal: split.vehiclesTotal,
              accessoriesTotal: split.accessoriesTotal,
              insuranceTotal: split.insuranceTotal,
              deliveryCost: split.deliveryCost,
            }
          : {}),
        paymentMethod: formData.paymentMethod,
        paymentStatus: isPartialPaymentSelected ? 'PAID' : formData.paymentStatus,
        employeeId: user?.id, // Añadir el ID del usuario al crear la reserva
        agencyId: formData.agencyId || undefined, // Añadir el ID de la agencia si el método es COLABORADOR
        ...(formData.paymentMethod === 'COLABORADOR' && formData.ticket?.trim()
          ? { ticket: formData.ticket.trim() }
          : {}),
        businessGroup: selectedBusinessGroup,
        businessSubgroup: undefined, // Opcional: BIKES, ACCESSORIES, INSURANCES para Quipu
        language,
        notes: formData.notes?.trim() ? formData.notes.trim() : undefined,
        ...(manualPct > 0 ? { manualDiscountPercent: manualPct } : {}),
        ...(selectedBusinessGroup === 'BIKES' && formData.paymentMethod !== 'COLABORADOR'
          ? {
              paymentMode:
                partialPaymentAllowed && formData.paymentMode === 'PARTIAL' ? 'PARTIAL' : 'FULL',
              ...(partialPaymentAllowed &&
              formData.paymentMode === 'PARTIAL' &&
              formData.partialDepositPercentOverride !== ''
                ? { partialDepositPercentOverride: parseInt(formData.partialDepositPercentOverride, 10) }
                : {}),
            }
          : {}),
      };

      const response = await authenticatedFetch(buildApiUrl(API_ENDPOINTS.BOOKINGS), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData)
      });

      const data = await response.json();

      if (data.success) {
        // Llamar a los callbacks para actualizar la lista
        onSuccess?.(data.data);
        onBookingCreated?.(data.data);
        // Mostrar pantalla de éxito (no se puede volver atrás)
        setReservationCreated(true);
      } else {
        setError(data.message || t.errorCreatingBooking);
      }
    } catch (error) {
      setError(error.message || t.errorCreatingBooking);
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Validar formulario según el paso actual
  const isFormValid = () => {
    // Validación específica por paso
    switch (step) {
      case 1: {
        // Paso 1: Solo fechas (nuevo primer paso)
        // Permitir que startDate sea igual a endDate (mismo día = 1 día de reserva)
        if (!formData.startDate || !formData.endDate) {
          return false;
        }
        const start = new Date(formData.startDate);
        const end = new Date(formData.endDate);
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        // startDate debe ser menor o igual a endDate
        if (start > end) return false;
        if (selectedBusinessGroup === 'CARRITOS') {
          const sdt = dateToStartDateTime(formData.startDate);
          const edt = dateToEndDateTime(formData.endDate);
          if (!sdt || !edt) return false;
          return new Date(sdt) < new Date(edt);
        }
        return true;
      }
      
      case 2: {
        // Paso 2: Fechas + vehículos seleccionados
        if (!formData.startDate ||
            !formData.endDate ||
            formData.selectedVehicles.length === 0) {
          return false;
        }
        const start2 = new Date(formData.startDate);
        const end2 = new Date(formData.endDate);
        start2.setHours(0, 0, 0, 0);
        end2.setHours(0, 0, 0, 0);
        if (start2 > end2) return false;
        if (selectedBusinessGroup === 'CARRITOS') {
          const sdt = dateToStartDateTime(formData.startDate);
          const edt = dateToEndDateTime(formData.endDate);
          if (!sdt || !edt) return false;
          return new Date(sdt) < new Date(edt);
        }
        return true;
      }
      
      case 3: {
        // Paso 3: accesorios (omitido en UI para CARRITOS)
        if (selectedBusinessGroup === 'CARRITOS') return true;
        if (!formData.startDate ||
            !formData.endDate ||
            formData.selectedVehicles.length === 0) {
          return false;
        }
        const start3 = new Date(formData.startDate);
        const end3 = new Date(formData.endDate);
        start3.setHours(0, 0, 0, 0);
        end3.setHours(0, 0, 0, 0);
        if (start3 > end3) return false;
        return true;
      }
      
      case 4: {
        // Paso 4: Datos del cliente (nuevo cuarto paso)
        const nameTrimmed = formData.customerName?.trim() || '';
        const nameOk = nameTrimmed.length >= 2 && nameTrimmed.length <= 100 && /^[\p{L}\s'-]+$/u.test(nameTrimmed);
        const prefixOk = formData.customerPhonePrefix?.trim() && /^\+?[1-9]\d{0,3}$/.test(formData.customerPhonePrefix.trim());
        const docOk = formData.customerDni?.trim() && /^([0-9]{8}[A-Z]|[XYZ][0-9]{7}[A-Z]|[A-Z0-9]{5,20})$/i.test(formData.customerDni.trim());
        const totalStep4 = calculateTotal();
        const addressOk = totalStep4 <= 2999.99 || (formData.customerAddress?.trim()?.length > 0);
        return (
          nameOk &&
          formData.customerEmail?.trim() &&
          prefixOk &&
          formData.customerPhone?.trim() &&
          docOk &&
          addressOk &&
          isValidEmail(formData.customerEmail) &&
          formData.startDate &&
          formData.endDate &&
          formData.selectedVehicles.length > 0
        );
      }
      
      case 5: {
        // Paso 5: Todo lo anterior + método de pago + políticas de cancelación
        const nameTrimmed5 = formData.customerName?.trim() || '';
        const nameValid = nameTrimmed5.length >= 2 && nameTrimmed5.length <= 100 && /^[\p{L}\s'-]+$/u.test(nameTrimmed5);
        const prefixValid = formData.customerPhonePrefix?.trim() && /^\+?[1-9]\d{0,3}$/.test(formData.customerPhonePrefix.trim());
        const docValid = formData.customerDni?.trim() && /^([0-9]{8}[A-Z]|[XYZ][0-9]{7}[A-Z]|[A-Z0-9]{5,20})$/i.test(formData.customerDni.trim());
        const totalStep5 = getTotalAfterManualDiscount();
        const addressValid = totalStep5 <= 2999.99 || (formData.customerAddress?.trim()?.length > 0);
        if (!nameValid ||
            !formData.customerEmail?.trim() || 
            !prefixValid ||
            !formData.customerPhone?.trim() || 
            !docValid ||
            !addressValid ||
            !isValidEmail(formData.customerEmail) ||
            !formData.startDate ||
            !formData.endDate ||
            formData.selectedVehicles.length === 0 ||
            !formData.paymentStatus ||
            !formData.paymentMethod?.trim() ||
            !acceptedCancellationPolicy) {
          return false;
        }
        // Si el método de pago es COLABORADOR, validar que se haya seleccionado una agencia
        if (formData.paymentMethod === 'COLABORADOR' && !formData.agencyId) {
          return false;
        }
        const start5 = new Date(formData.startDate);
        const end5 = new Date(formData.endDate);
        start5.setHours(0, 0, 0, 0);
        end5.setHours(0, 0, 0, 0);
        if (start5 > end5) return false;
        if (selectedBusinessGroup === 'CARRITOS') {
          const sdt = dateToStartDateTime(formData.startDate);
          const edt = dateToEndDateTime(formData.endDate);
          if (!sdt || !edt) return false;
          return new Date(sdt) < new Date(edt);
        }
        return true;
      }
      
      default:
        return false;
    }
  };

  // Función para validar email (formato correo electrónico)
  const isValidEmail = (email) => {
    if (!email || email.length > 254) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Normalizar prefijo: guardar con '+' delante si no lo tiene
  const normalizePhonePrefix = (prefix) => {
    if (!prefix || !prefix.trim()) return '+34';
    let p = prefix.trim();
    if (p.startsWith('00')) p = `+${p.slice(2)}`;
    return p.startsWith('+') ? p : `+${p}`;
  };

  // Función para formatear fecha de manera legible
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Función para formatear precio
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const totalWizardSteps = selectedBusinessGroup === 'CARRITOS' ? 4 : 5;
  const displayWizardStep =
    selectedBusinessGroup === 'CARRITOS' ? (step <= 2 ? step : step - 1) : step;

  return (
    <div className="crear-reserva-modal fullscreen">
      <div className="modal-content fullscreen">
        <div className="modal-header">
          <div className="header-content">
            <h2>{t.createBooking}</h2>
            {!reservationCreated && (
              <div className="progress-indicator">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${(displayWizardStep / totalWizardSteps) * 100}%` }}
                  ></div>
                </div>
                <span className="progress-text">
                  {t.step} {displayWizardStep} {t.of} {totalWizardSteps}
                </span>
              </div>
            )}
          </div>
          <div className="header-actions">
            <div className="language-selector">
              <Globe size={18} />
              <select 
                value={language} 
                onChange={(e) => setLanguage(e.target.value)}
                className="language-select"
              >
                <option value="es">Español</option>
                <option value="en">English</option>
                <option value="de">Deutsch</option>
              </select>
            </div>
            <button className="close-button" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        <div className="modal-body">
          {reservationCreated ? (
            /* Pantalla de éxito: no se puede volver atrás */
            <div className="crear-reserva-success-view">
              <div className="success-icon-wrap">
                <CheckCircle size={64} className="success-icon" />
              </div>
              <h3 className="success-title">{t.bookingCreatedSuccess}</h3>
              <p className="success-message">{t.success}</p>
              <button
                type="button"
                className="btn-primary btn-back-to-reservations"
                onClick={onClose}
              >
                <List size={18} />
                {t.backToReservations}
              </button>
            </div>
          ) : (
            <>
          {/* Mensaje de ayuda */}
          <div className="help-message">
            <span className="required-indicator">*</span> {t.requiredFields}
          </div>

          {/* Paso 1: Fechas y entrega (nuevo primer paso) */}
          {step === 1 && (
            <div className="step-content">
              <h3>{t.datesAndDelivery}</h3>

              <div className="form-grid" style={{ marginBottom: 16 }}>
                <div className="form-group">
                  <label>Tipo de vehículo <span className="required-indicator">*</span></label>
                  <select
                    value={selectedBusinessGroup}
                    onChange={(e) => setSelectedBusinessGroup(e.target.value)}
                  >
                    <option value="BIKES">Bicicletas</option>
                    <option value="CARRITOS">Carritos</option>
                  </select>
                </div>

              </div>
              
              {/* Date Range Picker */}
              <div className="date-range-picker">
                <div className="date-range-container">
                  <div className="date-input-wrapper">
                    <label className="date-label">
                      <Calendar size={18} />
                      {selectedBusinessGroup === 'CARRITOS' ? 'Día del alquiler' : t.startDate}{' '}
                      <span className="required-indicator">*</span>
                    </label>
                    <SingleDatePicker
                      id="start-date-input"
                      value={formData.startDate}
                      onChange={(v) => handleFieldChange('startDate', v)}
                      minDate={getCurrentDate()}
                      closedDates={closedDays}
                      hasError={Boolean(validationErrors.startDate)}
                      locale={datePickerLocale}
                    />
                    {validationErrors.startDate && (
                      <div className="error-message">{validationErrors.startDate}</div>
                    )}
                  </div>
                  
                  {selectedBusinessGroup !== 'CARRITOS' && (
                    <>
                  <div className="date-separator">
                    <span className="separator-line"></span>
                    <span className="separator-arrow">→</span>
                    <span className="separator-line"></span>
                  </div>
                  
                  <div className="date-input-wrapper">
                    <label className="date-label">
                      <Calendar size={18} />
                      {t.endDate} <span className="required-indicator">*</span>
                    </label>
                    <SingleDatePicker
                      id="end-date-input"
                      value={formData.endDate}
                      onChange={(v) => handleFieldChange('endDate', v)}
                      minDate={formData.startDate || getCurrentDate()}
                      closedDates={closedDays}
                      hasError={Boolean(validationErrors.endDate)}
                      disabled={selectedBusinessGroup === 'CARRITOS'}
                      locale={datePickerLocale}
                    />
                    {validationErrors.endDate && (
                      <div className="error-message">{validationErrors.endDate}</div>
                    )}
                  </div>
                    </>
                  )}
                </div>

                {selectedBusinessGroup === 'CARRITOS' && (
                  <div className="form-grid" style={{ marginTop: 16 }}>
                    <div className="form-group">
                      <label>Hora prevista de salida (informativa)</label>
                      <div
                        className="date-input-container"
                        onMouseDown={(e) => {
                          if (e.target.closest?.('.date-picker-button')) return;
                          const inp = e.currentTarget.querySelector('input');
                          if (!inp) return;
                          tryShowNativePicker(inp);
                        }}
                      >
                        <input
                          type="time"
                          id="cart-start-time-input"
                          className="date-input"
                          step={60}
                          value={formData.startTime}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, startTime: e.target.value }))
                          }
                          onClick={(e) => tryShowNativePicker(e.currentTarget)}
                          onFocus={(e) => tryShowNativePicker(e.currentTarget)}
                        />
                        <button
                          type="button"
                          className="date-picker-button"
                          onClick={(e) => {
                            e.preventDefault();
                            const input = document.getElementById('cart-start-time-input');
                            if (input) {
                              input.focus();
                              tryShowNativePicker(input);
                              if (typeof input.showPicker !== 'function') {
                                input.click();
                              }
                            }
                          }}
                          aria-label="Abrir selector de hora"
                        >
                          <Clock size={20} />
                        </button>
                      </div>
                      <small style={{ color: '#666', display: 'block', marginTop: 6 }}>
                        Hora y minutos a título orientativo en tienda. El precio sigue siendo por la franja de{' '}
                        {formData.cartRentalHours} h elegida abajo; el stock de carritos no se bloquea por horario.
                      </small>
                    </div>
                    <div className="form-group">
                      <label>Duración <span className="required-indicator">*</span></label>
                      <select
                        value={String(formData.cartRentalHours)}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            cartRentalHours: parseInt(e.target.value, 10) || 2,
                          }))
                        }
                      >
                        <option value="1">1 hora</option>
                        <option value="2">2 horas</option>
                        <option value="3">3 horas</option>
                        <option value="4">4 horas</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Fin del alquiler (calculado)</label>
                      <div
                        style={{
                          padding: '10px 12px',
                          border: '1px solid #ddd',
                          borderRadius: 8,
                          background: '#f8f9fa',
                          fontSize: '14px',
                        }}
                      >
                        {formData.endDate ? formatDate(formData.endDate) : '—'}{' '}
                        <strong>{formData.endTime || ''}</strong>
                      </div>
                      <small style={{ color: '#666' }}>
                        Precio según franja 1h–4h. Sin accesorios ni seguros.
                      </small>
                    </div>
                  </div>
                )}
                
                {formData.startDate && formData.endDate && (
                  <div className="duration-info">
                    <Clock size={16} />
                    <span>{t.duration}: <strong>{formatDuration()}</strong></span>
                    <button 
                      type="button"
                      className="reset-dates-btn"
                      onClick={() => {
                        if (selectedBusinessGroup === 'CARRITOS') {
                          const { startDate } = getDefaultDates();
                          setFormData((prev) => ({
                            ...prev,
                            startDate,
                            endDate: startDate,
                            startTime: '09:00',
                            cartRentalHours: 2,
                          }));
                        } else {
                          setFormData((prev) => ({
                            ...prev,
                            ...getDefaultDates(),
                          }));
                        }
                        // Limpiar errores de validación de fechas
                        setValidationErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.startDate;
                          delete newErrors.endDate;
                          return newErrors;
                        });
                      }}
                    >
                      {t.resetDates}
                    </button>
                  </div>
                )}
              </div>
              
              {selectedBusinessGroup === 'CARRITOS' ? (
                <div className="form-group" style={{ marginTop: 8 }}>
                  <label>Entrega</label>
                  <p style={{ margin: 0, color: '#444', fontSize: 15, lineHeight: 1.5 }}>
                    Las reservas de carrito son <strong>siempre en tienda</strong> ({t.pickup}).
                  </p>
                </div>
              ) : (
                <div className="form-grid">
                  <div className="form-group">
                    <label>{t.deliveryType}</label>
                    <select
                      value={formData.deliveryType}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          deliveryType: e.target.value,
                          ...(e.target.value === 'PICKUP' && {
                            deliveryAddress: '',
                            deliveryDistance: null,
                            deliveryCost: null,
                          }),
                        }));
                      }}
                    >
                      <option value="PICKUP">{t.pickup}</option>
                      <option value="DELIVERY">{t.delivery}</option>
                    </select>
                  </div>

                  {formData.deliveryType === 'DELIVERY' && (
                    <div className="delivery-map-section">
                      <MapaEntrega
                        onAddressChange={(address) => {
                          setFormData((prev) => ({ ...prev, deliveryAddress: address }));
                        }}
                        onDistanceChange={(distance) => {
                          setFormData((prev) => ({ ...prev, deliveryDistance: distance }));
                        }}
                        onDeliveryCostChange={(cost) => {
                          setFormData((prev) => ({ ...prev, deliveryCost: cost }));
                        }}
                        initialAddress={formData.deliveryAddress}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Paso 2: Selección de vehículos */}
          {step === 2 && (
            <div className="step-content step-with-summary">
              <div className="step-main-content">
                <h3>{t.vehicleSelection}</h3>
                
                {loading ? (
                <div className="loading">
                  <Loader size={20} />
                  {t.loadingVehicles}
                </div>
              ) : (
                <>
                  <div className="filter-section">
                    <div className="filters-grid">
                      {selectedBusinessGroup !== 'CARRITOS' && (
                      <DateRangeFilter
                        idPrefix="crear-reserva-bikes"
                        label={`${t.bookingDateRangeFilterLabel}:`}
                        labelClassName="filter-label"
                        from={formData.startDate}
                        to={formData.endDate}
                        onRangeChange={handleBookingDateRangeChange}
                        dialogAriaLabel={t.bookingDateRangeFilterLabel}
                        hideClearButton
                        disabledBefore={new Date()}
                        closedDates={closedDays}
                        emptySummary={t.bookingDateRangePlaceholder}
                        locale={datePickerLocale}
                      />
                      )}
                      <div className="filter-group">
                        <label className="filter-label">{t.category}:</label>
                        <select
                          className="filter-select"
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          disabled={categories.length === 0}
                        >
                          <option value="">{t.allCategories}</option>
                          {categories.length > 0 ? (
                            categories
                              .filter(cat => !cat.businessGroup || cat.businessGroup === selectedBusinessGroup)
                              .map(category => (
                                <option key={category.id} value={category.id}>
                                  {category.name}
                                </option>
                              ))
                          ) : (
                            <option value="" disabled>Cargando categorías...</option>
                          )}
                        </select>
                        {categories.length === 0 && (
                          <small className="filter-hint">No hay categorías disponibles</small>
                        )}
                      </div>
                      
                      {selectedBusinessGroup !== 'CARRITOS' && (
                      <div className="filter-group">
                        <label className="filter-label">{t.size}:</label>
                        <select
                          className="filter-select"
                          value={selectedSize}
                          onChange={(e) => setSelectedSize(e.target.value)}
                        >
                          <option value="">{t.allSizes || 'Todas las tallas'}</option>
                          <option value="XS">XS</option>
                          <option value="S">S</option>
                          <option value="M">M</option>
                          <option value="ML">ML</option>
                          <option value="L">L</option>
                          <option value="XL">XL</option>
                          <option value="XXL">XXL</option>
                        </select>
                      </div>
                      )}
                    </div>
                  </div>

                  {/* Mostrar modelos con selección de tallas */}
                  {(() => {
                    // Filtrar modelos por categoría
                    const filteredModels = availableVehicles.filter(model => {
                      if (selectedCategory) {
                        const categoryId = model.category?.id || model.categoryId;
                        return categoryId === parseInt(selectedCategory);
                      }
                      return true;
                    });

                    // Bicis: agrupar por talla. Carritos: un solo bloque por modelo (sin talla en UI).
                    const modelsWithSizes =
                      selectedBusinessGroup === 'CARRITOS'
                        ? filteredModels
                            .map((model) => ({
                              ...model,
                              availableSizes: model.vehicles?.length ? ['__CART__'] : [],
                              vehiclesBySize: model.vehicles?.length
                                ? { __CART__: model.vehicles }
                                : {},
                            }))
                            .filter((model) => model.availableSizes.length > 0)
                        : filteredModels
                            .map((model) => {
                              const vehiclesBySize = {};
                              const sizeOrder = ['XS', 'S', 'M', 'ML', 'L', 'XL', 'XXL'];

                              model.vehicles.forEach((vehicle) => {
                                if (selectedSize && vehicle.size !== selectedSize) {
                                  return;
                                }

                                const size = vehicle.size;
                                if (!vehiclesBySize[size]) {
                                  vehiclesBySize[size] = [];
                                }
                                vehiclesBySize[size].push(vehicle);
                              });

                              const availableSizes = Object.keys(vehiclesBySize)
                                .filter((size) => vehiclesBySize[size].length > 0)
                                .sort((a, b) => {
                                  const indexA = sizeOrder.indexOf(a);
                                  const indexB = sizeOrder.indexOf(b);
                                  if (indexA === -1 && indexB === -1) return a.localeCompare(b);
                                  if (indexA === -1) return 1;
                                  if (indexB === -1) return -1;
                                  return indexA - indexB;
                                });

                              return {
                                ...model,
                                availableSizes,
                                vehiclesBySize,
                              };
                            })
                            .filter((model) => model.availableSizes.length > 0);

                    // Si no hay modelos después de filtrar, mostrar mensaje
                    if (modelsWithSizes.length === 0) {
                      return (
                        <div className="no-vehicles-message">
                          <Bike size={48} />
                          <p>{t.noVehiclesFound || 'No se encontraron vehículos con los filtros seleccionados'}</p>
                          <button
                            className="clear-filters-btn"
                            onClick={() => {
                              setSelectedCategory('');
                              setSelectedSize('');
                            }}
                          >
                            {t.clearFilters || 'Limpiar filtros'}
                          </button>
                        </div>
                      );
                    }

                    return (
                      <div className="models-grid">
                        {modelsWithSizes.map(model => {
                          const durationInDays = calculateDuration();
                          const pricePerDay = getPricePerDay(model, durationInDays);
                          const totalSelected = model.availableSizes.reduce((total, size) => {
                            return total + getSelectedCount(model.id, size);
                          }, 0);

                          return (
                            <div key={model.id} className="model-card">
                              {/* Imagen del modelo */}
                              {model.imageUrls && model.imageUrls.length > 0 && (
                                <div className="model-image-container">
                                  <img 
                                    src={model.imageUrls[0]} 
                                    alt={model.name}
                                    className="model-image"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                </div>
                              )}
                              
                              {/* Información del modelo */}
                              <div className="model-content">
                                <div className="model-header">
                                  <h4 className="model-name">{model.name}</h4>
                                  {model.category && (
                                    <span className="model-category">{model.category.name}</span>
                                  )}
                                </div>
                                
                                {model.description && (
                                  <p className="model-description">{model.description}</p>
                                )}
                                
                                {/* Precio */}
                                <div className="model-pricing">
                                  <span className={`price-tag ${model.hasSeasonalPricing ? 'seasonal' : ''}`}>
                                    {selectedBusinessGroup === 'CARRITOS' ? (
                                      <>
                                        {formatPrice(pricePerDay)}
                                        <span style={{ fontWeight: 500, fontSize: '0.9em' }}>
                                          {' '}
                                          ({formatDuration()})
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        {formatPrice(pricePerDay)}
                                        {t.perDay}
                                      </>
                                    )}
                                  </span>
                                  {selectedBusinessGroup !== 'CARRITOS' &&
                                  model.pricingType !== 'CART_HOURLY' &&
                                  durationInDays >= 3 && durationInDays <= 5 && model.threeToFive > 0 && !model.hasSeasonalPricing && (
                                    <span className="discount-indicator">
                                      ({t.discount} {model.threeToFive}%)
                                    </span>
                                  )}
                                </div>

                                {/* Indicador de selección */}
                                {totalSelected > 0 && (
                                  <div className="selection-indicator">
                                    {totalSelected}{' '}
                                    {selectedBusinessGroup === 'CARRITOS'
                                      ? totalSelected === 1
                                        ? 'carrito seleccionado'
                                        : 'carritos seleccionados'
                                      : totalSelected === 1
                                        ? 'bicicleta seleccionada'
                                        : 'bicicletas seleccionadas'}
                                  </div>
                                )}

                                {/* Selección por talla (bicis) o por unidades (carritos) */}
                                <div className="sizes-section">
                                  <h5 className="sizes-title">
                                    {selectedBusinessGroup === 'CARRITOS'
                                      ? 'Unidades'
                                      : t.selectSize || 'Seleccionar talla'}
                                  </h5>
                                  <div className="sizes-grid">
                                    {model.availableSizes.map((size) => {
                                      const availableCount = model.vehiclesBySize[size].length;
                                      const selectedCount = getSelectedCount(model.id, size);
                                      const isAvailable = availableCount > 0;
                                      const isCartBucket =
                                        selectedBusinessGroup === 'CARRITOS' && size === '__CART__';

                                      return (
                                        <div
                                          key={size}
                                          className={`size-card ${!isAvailable ? 'unavailable' : ''} ${selectedCount > 0 ? 'selected' : ''}`}
                                        >
                                          <div className="size-card-header">
                                            {isCartBucket ? (
                                              <span className="size-label">
                                                Disponibles: {availableCount}
                                              </span>
                                            ) : (
                                              <>
                                                <span className="size-label">{size}</span>
                                                <span className="size-count">({availableCount})</span>
                                              </>
                                            )}
                                          </div>
                                          <div className="size-controls">
                                            <button
                                              className="size-button"
                                              disabled={!isAvailable || selectedCount === 0}
                                              onClick={() => handleVehicleSelection(model.id, size, 'remove')}
                                              aria-label={
                                                isCartBucket
                                                  ? 'Quitar un carrito'
                                                  : `Reducir cantidad de talla ${size}`
                                              }
                                            >
                                              <Minus size={14} />
                                            </button>
                                            <span className="size-quantity">{selectedCount}</span>
                                            <button
                                              className="size-button"
                                              disabled={!isAvailable || selectedCount >= availableCount}
                                              onClick={() => handleVehicleSelection(model.id, size, 'add')}
                                              aria-label={
                                                isCartBucket
                                                  ? 'Añadir un carrito'
                                                  : `Aumentar cantidad de talla ${size}`
                                              }
                                            >
                                              <Plus size={14} />
                                            </button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {formData.selectedVehicles.length > 0 && (
                    <div className="selected-vehicles visual">
                      <h4>{t.selectedVehicles}:</h4>
                      <div className="selected-list">
                        {formData.selectedVehicles.map((vehicle) => (
                          <div key={vehicle.vehicleId} className="selected-item visual">
                            <Bike size={18} />
                            <div className="selected-info">
                              <span className="selected-name">
                                {vehicle.modelName}
                                {selectedBusinessGroup !== 'CARRITOS' && ` — ${vehicle.size}`}
                              </span>
                              <span className="serial">
                                {t.serialNumber}: {vehicle.serialNumber}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
              </div>
              
              {/* Resumen de reserva en lateral derecho */}
              {(formData.startDate && formData.endDate) && (
                <div className="step-summary-sidebar">
                  <ResumenReserva
                    formData={formData}
                    availableVehicles={availableVehicles}
                    accessories={accessories}
                    accessoriesByModel={accessoriesByModel}
                    calculateDuration={calculateDuration}
                    calculateVehiclePrice={calculateVehiclePrice}
                    calculateAccessoryPrice={calculateAccessoryPrice}
                    calculateTotal={calculateTotal}
                    formatPrice={formatPrice}
                    formatDate={formatDate}
                    t={t}
                    language={language}
                    showInsuranceWarnings={step >= 3 && selectedBusinessGroup !== 'CARRITOS'}
                  />
                </div>
              )}
            </div>
          )}

          {/* Paso 3: Accesorios (solo bicis) */}
          {step === 3 && selectedBusinessGroup !== 'CARRITOS' && (
            <div className="step-content step-with-summary">
              <div className="step-main-content">
                <h3>{t.accessories}</h3>

                {selectedBusinessGroup === 'CARRITOS' ? (
                  <div className="no-accessories">
                    <Package size={48} />
                    <p>Los carritos no tienen accesorios asociados.</p>
                  </div>
                ) : formData.selectedVehicles.length > 0 ? (
                  <div className="accessories-section">
                    {formData.selectedVehicles.map(vehicle => {
                    const modelAccessories = accessoriesByModel[vehicle.modelId] || [];
                    const isLoadingModel = loadingAccessories[vehicle.modelId] || false;
                    
                    return (
                      <div key={vehicle.vehicleId} className="vehicle-accessories-group">
                        <div className="vehicle-accessories-header">
                          <h4>
                            <Bike size={20} />
                            {vehicle.modelName} - {vehicle.size}
                          </h4>
                          <span className="accessories-count">
                            {modelAccessories.length} {modelAccessories.length === 1 ? 'accesorio disponible' : 'accesorios disponibles'}
                          </span>
                        </div>
                        
                        {isLoadingModel ? (
                          <div className="loading-accessories">
                            <Loader size={20} />
                            <span>Cargando accesorios...</span>
                          </div>
                        ) : modelAccessories.length === 0 ? (
                          <div className="no-accessories">
                            <Package size={48} />
                            <p>No hay accesorios disponibles para este modelo de bicicleta.</p>
                          </div>
                        ) : (
                          <div className="accessories-grid-by-model">
                            {modelAccessories.map(accessory => {
                              const isSelected = formData.selectedAccessories[vehicle.vehicleId]?.includes(accessory.id);
                              
                              return (
                                <div 
                                  key={accessory.id} 
                                  className={`accessory-card-by-model visual ${isSelected ? 'selected' : ''}`}
                                  onClick={() => handleAccessoryToggle(accessory.id, vehicle.vehicleId)}
                                >
                                  {accessory.imageUrl && (
                                    <div className="accessory-image-container-model">
                                      <img 
                                        src={accessory.imageUrl} 
                                        alt={accessory.name}
                                        className="accessory-image-model"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    </div>
                                  )}
                                  <div className="accessory-content-model">
                                    <div className="accessory-header-model">
                                      <h5 className="accessory-name-model">{accessory.name}</h5>
                                      {accessory.category && (
                                        <span className="accessory-category-badge">
                                          {accessory.category}
                                        </span>
                                      )}
                                    </div>
                                    
                                    {accessory.description && (
                                      <p className="accessory-description-model">
                                        {accessory.description}
                                      </p>
                                    )}
                                    
                                    <div className="accessory-pricing-model">
                                      <div className="accessory-price-info">
                                        <span className="accessory-price-type">
                                          {accessory.accessoryPriceType === 'FIXED' 
                                            ? t.fixedPrice || 'Precio fijo'
                                            : t.dailyPrice || 'Por día'
                                          }
                                        </span>
                                        <span className="accessory-price-value">
                                          {getAccessoryPriceText(accessory)}
                                        </span>
                                      </div>
                                    </div>
                                    
                                    <button
                                      className={`select-button-accessory ${isSelected ? 'selected' : ''}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAccessoryToggle(accessory.id, vehicle.vehicleId);
                                      }}
                                    >
                                      {isSelected ? (
                                        <>
                                          <Minus size={16} />
                                          <span>{t.remove || 'Quitar'}</span>
                                        </>
                                      ) : (
                                        <>
                                          <Plus size={16} />
                                          <span>{t.add || 'Añadir'}</span>
                                        </>
                                      )}
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                    })}
                  </div>
                ) : (
                  <div className="no-vehicles-message">
                    <Bike size={48} />
                    <p>{t.selectVehiclesFirst}</p>
                  </div>
                )}
              </div>
              
              {/* Resumen de reserva en lateral derecho */}
              {(formData.startDate && formData.endDate) && (
                <div className="step-summary-sidebar">
                  <ResumenReserva
                    formData={formData}
                    availableVehicles={availableVehicles}
                    accessories={accessories}
                    accessoriesByModel={accessoriesByModel}
                    calculateDuration={calculateDuration}
                    calculateVehiclePrice={calculateVehiclePrice}
                    calculateAccessoryPrice={calculateAccessoryPrice}
                    calculateTotal={calculateTotal}
                    formatPrice={formatPrice}
                    formatDate={formatDate}
                    t={t}
                    language={language}
                    showInsuranceWarnings={step >= 3 && selectedBusinessGroup !== 'CARRITOS'}
                  />
                </div>
              )}
            </div>
          )}

          {/* Paso 4: Datos del cliente (nuevo cuarto paso) */}
          {step === 4 && (
            <div className="step-content step-with-summary">
              <div className="step-main-content">
                <h3>{t.customerData}</h3>
                <div className="form-grid">
                  {/* Fila 1: Nombre completo y Email */}
                  <div className="form-group">
                    <label>{t.fullName} <span className="required-indicator">*</span></label>
                    <input
                      type="text"
                      className={validationErrors.customerName ? 'error' : ''}
                      value={formData.customerName}
                      onChange={(e) => handleFieldChange('customerName', e.target.value)}
                      placeholder={t.customerNamePlaceholder}
                      maxLength={100}
                    />
                    {validationErrors.customerName && (
                      <div className="error-message">{validationErrors.customerName}</div>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label>{t.email} <span className="required-indicator">*</span></label>
                    <input
                      type="email"
                      className={validationErrors.customerEmail ? 'error' : ''}
                      value={formData.customerEmail}
                      onChange={(e) => handleFieldChange('customerEmail', e.target.value)}
                      placeholder={t.emailPlaceholder}
                      maxLength={254}
                    />
                    {validationErrors.customerEmail && (
                      <div className="error-message">{validationErrors.customerEmail}</div>
                    )}
                  </div>
                  
                  {/* Fila 2: Prefijo+Teléfono (misma columna que Nombre) | DNI (misma columna que Email) */}
                  <div className="form-cell-prefix-phone">
                    <div className="form-group form-group-prefix">
                      <label>{t.phonePrefixLabel} <span className="required-indicator">*</span></label>
                      <input
                        type="tel"
                        className={validationErrors.customerPhonePrefix ? 'error' : ''}
                        value={formData.customerPhonePrefix}
                        onChange={(e) => handleFieldChange('customerPhonePrefix', e.target.value)}
                        placeholder={t.phonePrefixPlaceholder}
                        maxLength={5}
                      />
                    </div>
                    <div className="form-group form-group-phone">
                      <label>{t.phone} <span className="required-indicator">*</span></label>
                      <input
                        type="tel"
                        className={validationErrors.customerPhone ? 'error' : ''}
                        value={formData.customerPhone}
                        onChange={(e) => handleFieldChange('customerPhone', e.target.value)}
                        placeholder={t.phonePlaceholder}
                      />
                    </div>
                  </div>
                  <div className="form-group form-group-dni">
                    <label>{t.idDocumentLabel} <span className="required-indicator">*</span></label>
                    <input
                      type="text"
                      className={validationErrors.customerDni ? 'error' : ''}
                      value={formData.customerDni}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        handleFieldChange('customerDni', value);
                      }}
                      placeholder={t.idDocumentPlaceholder}
                      maxLength={20}
                    />
                  </div>
                  {(validationErrors.customerPhone || validationErrors.customerPhonePrefix || validationErrors.customerDni) && (
                    <div className="form-row-errors">
                      <div className="error-message">
                        {validationErrors.customerPhonePrefix || validationErrors.customerPhone || validationErrors.customerDni}
                      </div>
                    </div>
                  )}
                  {/* Dirección del cliente (obligatoria si total > 2.999,99 €) */}
                  {calculateTotal() > 2999.99 && (
                    <div className="form-group form-group-full-width">
                      <label>{t.addressLabel} <span className="required-indicator">*</span></label>
                      <input
                        type="text"
                        className={validationErrors.customerAddress ? 'error' : ''}
                        value={formData.customerAddress}
                        onChange={(e) => handleFieldChange('customerAddress', e.target.value)}
                        onBlur={() => validateField('customerAddress', formData.customerAddress)}
                        placeholder={t.addressPlaceholder}
                      />
                      {validationErrors.customerAddress && (
                        <div className="error-message">{validationErrors.customerAddress}</div>
                      )}
                    </div>
                  )}
                </div>

                {(() => {
                  // Mostrar inputs de entrepierna solo si hay vehículos seleccionados con el flag activo en su modelo
                  const eligible = (formData.selectedVehicles || []).filter((v) => {
                    const model = availableVehicles.find((m) => m?.id === v?.modelId);
                    return Boolean(model?.inseamOptionalEnabled);
                  });
                  if (eligible.length === 0) return null;

                  const currentMap = formData.inseamCmByVehicleId || {};

                  return (
                    <div style={{ marginTop: '18px' }}>
                      <h4 style={{ margin: '0 0 8px 0', color: '#111827', fontSize: '15px' }}>
                        Medida de entrepierna (opcional)
                      </h4>
                      <p style={{ margin: '0 0 12px 0', color: '#6b7280', fontSize: '13px' }}>
                        Puedes indicar la entrepierna (cm) para las bicicletas cuyo modelo lo permita.
                      </p>
                      <div className="form-grid">
                        {eligible.map((v) => {
                          const vehicleId = v.vehicleId;
                          const label = `${v.modelName || 'Modelo'} (${v.size || '—'})`;
                          return (
                            <div key={vehicleId} className="form-group">
                              <label>
                                Entrepierna (cm) — {label}
                              </label>
                              <input
                                type="number"
                                inputMode="decimal"
                                min={30}
                                max={120}
                                step={0.5}
                                value={currentMap[vehicleId] ?? ''}
                                onKeyDown={(e) => {
                                  if (e.key === '-' || e.key === '+') e.preventDefault();
                                }}
                                onChange={(e) => {
                                  const nextRaw = e.target.value;
                                  if (typeof nextRaw === 'string' && nextRaw.trim().startsWith('-')) return;
                                  setFormData((prev) => ({
                                    ...prev,
                                    inseamCmByVehicleId: {
                                      ...(prev.inseamCmByVehicleId || {}),
                                      [vehicleId]: nextRaw
                                    }
                                  }));
                                }}
                                placeholder="Ej. 82"
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
              
              {/* Resumen de reserva en lateral derecho */}
              {(formData.startDate && formData.endDate) && (
                <div className="step-summary-sidebar">
                  <ResumenReserva
                    formData={formData}
                    availableVehicles={availableVehicles}
                    accessories={accessories}
                    accessoriesByModel={accessoriesByModel}
                    calculateDuration={calculateDuration}
                    calculateVehiclePrice={calculateVehiclePrice}
                    calculateAccessoryPrice={calculateAccessoryPrice}
                    calculateTotal={calculateTotal}
                    formatPrice={formatPrice}
                    formatDate={formatDate}
                    t={t}
                    language={language}
                    showInsuranceWarnings={step >= 3 && selectedBusinessGroup !== 'CARRITOS'}
                  />
                </div>
              )}
            </div>
          )}

          {/* Paso 5: Pago y confirmación */}
          {step === 5 && (
            <div className="step-content">
              <h3>{t.paymentAndConfirmation}</h3>
              
              <div className="payment-section">
                <div className="form-group">
                  <label>{t.paymentStatus} <span className="required">*</span></label>
                  <select
                    value={isPartialPaymentSelected ? 'PAID' : formData.paymentStatus}
                    onChange={(e) => setFormData(prev => ({ ...prev, paymentStatus: e.target.value }))}
                    disabled={isPartialPaymentSelected}
                    required
                  >
                    <option value="PAID">{t.paid}</option>
                    <option value="PENDING">{t.pending}</option>
                    <option value="FAILED">{t.failed}</option>
                  </select>
                  {isPartialPaymentSelected && (
                    <p className="payment-mode-min-total-msg" role="note">
                      Con anticipo + liquidación el estado de pago es siempre «Pagado».
                    </p>
                  )}
                </div>
                
                <div className="form-group">
                  <label>
                    {t.notes || 'Notas'}{' '}
                    <span className="optional">({t.optional || 'Opcional'})</span>
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => {
                      const value = e.target.value.slice(0, 500);
                      setFormData(prev => ({ ...prev, notes: value }));
                    }}
                    rows={3}
                    maxLength={500}
                    placeholder={t.notesPlaceholder || 'Añade una nota interna sobre esta reserva (máx. 500 caracteres)'}
                  />
                  <small style={{ color: '#6b7280' }}>
                    {(formData.notes || '').length}/500 caracteres
                  </small>
                </div>

                <div className="form-group">
                  <label>
                    {t.manualDiscountLabel || 'Descuento manual'}{' '}
                    <span className="optional">({t.optional || 'Opcional'})</span>
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.manualDiscountPercent}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, manualDiscountPercent: e.target.value }));
                      }}
                      onBlur={() => {
                        const raw = formData.manualDiscountPercent;
                        if (raw === '' || String(raw).trim() === '') {
                          setFormData(prev => ({ ...prev, manualDiscountPercent: '' }));
                          return;
                        }
                        const n = parseFloat(String(raw).replace(',', '.'));
                        if (!Number.isFinite(n) || n <= 0) {
                          setFormData(prev => ({ ...prev, manualDiscountPercent: '' }));
                          return;
                        }
                        const clamped = Math.min(100, n);
                        setFormData(prev => ({ ...prev, manualDiscountPercent: String(clamped) }));
                      }}
                      placeholder="0"
                      style={{ maxWidth: '120px' }}
                    />
                    <span style={{ color: '#6b7280' }}>%</span>
                  </div>
                  {getParsedManualDiscountPercent() > 0 && (
                    <div
                      style={{
                        marginTop: '12px',
                        padding: '12px 14px',
                        backgroundColor: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        maxWidth: '420px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: '16px',
                          marginBottom: '8px',
                          color: '#374151',
                        }}
                      >
                        <span>{t.manualDiscountPreviewSubtotal || 'Total sin descuento'}</span>
                        <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                          {formatPrice(getSubtotalBeforeManualDiscount())}
                        </span>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: '16px',
                          marginBottom: '8px',
                          color: '#047857',
                        }}
                      >
                        <span>
                          {t.manualDiscountPreviewDiscount || 'Descuento'} (
                          {getParsedManualDiscountPercent()}%)
                        </span>
                        <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                          −{formatPrice(getManualDiscountEuros())}
                        </span>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: '16px',
                          paddingTop: '8px',
                          borderTop: '1px solid #bbf7d0',
                          color: '#111827',
                          fontWeight: 600,
                        }}
                      >
                        <span>{t.manualDiscountPreviewFinal || 'Total con descuento'}</span>
                        <span style={{ whiteSpace: 'nowrap' }}>
                          {formatPrice(getTotalAfterManualDiscount())}
                        </span>
                      </div>
                    </div>
                  )}
                  <small style={{ color: '#6b7280', display: 'block', marginTop: '4px' }}>
                    {t.manualDiscountHint ||
                      'Se aplica sobre el subtotal (bicis, accesorios y entrega) con IVA. Quedará registrado en la reserva.'}
                  </small>
                </div>

                <div className="form-group">
                  <label>{t.paymentMethod} <span className="required">*</span></label>
                  <select
                    value={formData.paymentMethod}
                    required
                    onChange={(e) => {
                      const newMethod = e.target.value;
                      setFormData(prev => ({ 
                        ...prev, 
                        paymentMethod: newMethod,
                        // Limpiar agencyId si no es 'COLABORADOR'
                        agencyId: newMethod === 'COLABORADOR' ? prev.agencyId : null,
                        ticket: newMethod === 'COLABORADOR' ? prev.ticket : '',
                      }));
                      // Limpiar selección de agencia si cambia el método
                      if (newMethod !== 'COLABORADOR') {
                        setSelectedAgencyId('');
                      }
                    }}
                    disabled={user?.role === 'AGENCY'}
                  >
                    <option value="">{t.selectPaymentMethod || 'Seleccionar método'}</option>
                    <option value="CASH">{t.cash}</option>
                    <option value="CARD">{t.card}</option>
                    <option value="TRANSFER">{t.transfer}</option>
                    <option value="BIZUM">{t.bizum}</option>
                    <option value="COLABORADOR">Colaborador</option>
                  </select>
                  {user?.role === 'AGENCY' && (
                    <small style={{ color: '#6b7280', display: 'block', marginTop: '4px' }}>
                      Los usuarios de agencia solo pueden crear reservas con método de pago Colaborador
                    </small>
                  )}
                </div>

                {selectedBusinessGroup === 'BIKES' && formData.paymentMethod !== 'COLABORADOR' && user?.role !== 'AGENCY' && (
                  <div className="form-group">
                    <label>Modalidad de cobro (bicicletas)</label>
                    <div
                      className="payment-mode-segmented"
                      role="radiogroup"
                      aria-label="Modalidad de cobro"
                    >
                      <button
                        type="button"
                        role="radio"
                        aria-checked={formData.paymentMode === 'FULL'}
                        className={`payment-mode-option${formData.paymentMode === 'FULL' ? ' active' : ''}`}
                        onClick={() => setFormData(prev => ({ ...prev, paymentMode: 'FULL' }))}
                      >
                        <CreditCard size={16} aria-hidden />
                        <span className="payment-mode-option-title">Pago único</span>
                        <span className="payment-mode-option-desc">Todo el importe en un cobro</span>
                      </button>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={formData.paymentMode === 'PARTIAL'}
                        aria-disabled={!partialPaymentAllowed}
                        disabled={!partialPaymentAllowed}
                        className={`payment-mode-option${formData.paymentMode === 'PARTIAL' ? ' active' : ''}${!partialPaymentAllowed ? ' disabled' : ''}`}
                        onClick={() => {
                          if (!partialPaymentAllowed) return;
                          // Anticipo cobrado en el momento → estado Pagado (de momento no existe "pagado parcial")
                          setFormData(prev => ({ ...prev, paymentMode: 'PARTIAL', paymentStatus: 'PAID' }));
                        }}
                      >
                        <Receipt size={16} aria-hidden />
                        <span className="payment-mode-option-title">Anticipo + liquidación</span>
                        <span className="payment-mode-option-desc">Primer cobro en € enteros, resto después</span>
                      </button>
                    </div>
                    {!partialPaymentAllowed && (
                      <p className="payment-mode-min-total-msg" role="note">
                        El pago parcial solo está disponible cuando el total de la reserva supera{' '}
                        {MIN_PARTIAL_TOTAL_EUR}€.
                      </p>
                    )}
                    {partialPaymentAllowed && formData.paymentMode === 'PARTIAL' && (
                      <div className="partial-percent-field">
                        <label htmlFor="partial-deposit-override">% de anticipo (opcional)</label>
                        <div className="partial-percent-row">
                          <input
                            id="partial-deposit-override"
                            type="number"
                            min={1}
                            max={100}
                            step={1}
                            value={formData.partialDepositPercentOverride}
                            onChange={(e) => setFormData(prev => ({ ...prev, partialDepositPercentOverride: e.target.value }))}
                            placeholder={`Por defecto: ${settingsPartialPercent}% (Ajustes)`}
                          />
                          <span className="partial-percent-suffix">%</span>
                        </div>
                        <small className="partial-percent-hint">
                          Vacío = se usa el {settingsPartialPercent}% configurado en Ajustes. El anticipo se redondea a euros enteros; el total de la reserva no cambia.
                        </small>
                      </div>
                    )}
                    <small className="payment-mode-footnote">
                      Con anticipo, el primer cobro en caja/TPV puede ser solo el anticipo; el cliente puede liquidar el saldo después (p. ej. por Stripe).
                    </small>
                  </div>
                )}
                
                {formData.paymentMethod === 'COLABORADOR' && (
                  <>
                  <div className="form-group">
                    <label>Agencia <span className="required">*</span></label>
                    <select
                      value={selectedAgencyId}
                      onChange={(e) => {
                        const agencyId = e.target.value;
                        setSelectedAgencyId(agencyId);
                        setFormData(prev => ({ ...prev, agencyId: agencyId ? parseInt(agencyId) : null }));
                      }}
                      required
                      disabled={user?.role === 'AGENCY'}
                    >
                      <option value="">Seleccionar agencia</option>
                      {agencies.map(agency => (
                        <option key={agency.id} value={agency.id}>
                          {agency.name} ({agency.code})
                        </option>
                      ))}
                    </select>
                    {user?.role === 'AGENCY' && (
                      <small style={{ color: '#6b7280', display: 'block', marginTop: '4px' }}>
                        Solo puedes crear reservas para tu propia agencia
                      </small>
                    )}
                    {agencies.length === 0 && (
                      <small style={{ color: '#dc3545' }}>
                        No hay agencias disponibles. Por favor, crea una agencia primero.
                      </small>
                    )}
                  </div>
                  <div className="form-group">
                    <label>{t.collaboratorTicket || 'Ticket'} <span className="optional">({t.optional || 'Opcional'})</span></label>
                    <input
                      type="text"
                      value={formData.ticket}
                      onChange={(e) => setFormData(prev => ({ ...prev, ticket: e.target.value }))}
                      placeholder={t.collaboratorTicketPlaceholder || 'Nº de ticket o referencia'}
                      maxLength={120}
                      autoComplete="off"
                    />
                  </div>
                  </>
                )}
              </div>

              <div className="booking-summary">
                <h4>{t.bookingSummary}</h4>
                
                {/* Información del cliente y fechas */}
                <div className="summary-section">
                  <div className="summary-item">
                    <span>{t.client}:</span>
                    <span>{formData.customerName}</span>
                  </div>
                  <div className="summary-item">
                    <span>{t.start}:</span>
                    <span>{formatDate(formData.startDate)}</span>
                  </div>
                  <div className="summary-item">
                    <span>{t.end}:</span>
                    <span>{formatDate(formData.endDate)}</span>
                  </div>
                  <div className="summary-item">
                    <span>{t.duration}:</span>
                    <span>{formatDuration()}</span>
                  </div>
                </div>

                {/* Vehículos seleccionados con detalles */}
                <div className="summary-section">
                  <h5 className="summary-section-title">
                    <Bike size={18} />
                    {t.vehicles} ({formData.selectedVehicles.length})
                  </h5>
                  {formData.selectedVehicles.map(vehicle => {
                    const vehicleModel = availableVehicles.find(v => v.id === vehicle.modelId);
                    const vehiclePrice = vehicleModel ? calculateVehiclePrice(vehicleModel, calculateDuration()) : 0;
                    
                    return (
                      <div key={vehicle.vehicleId} className="summary-vehicle-item">
                        <div className="summary-vehicle-header">
                          <div className="summary-vehicle-info">
                            <span className="summary-vehicle-name">{vehicle.modelName}</span>
                            <span className="summary-vehicle-details">
                              {t.size}: {vehicle.size} • {t.serialNumber}: {vehicle.serialNumber}
                            </span>
                          </div>
                          <span className="summary-vehicle-price">{formatPrice(vehiclePrice)}</span>
                        </div>
                        
                        {/* Accesorios de este vehículo */}
                        {formData.selectedAccessories[vehicle.vehicleId]?.length > 0 && (() => {
                          const vehicleAccessories = formData.selectedAccessories[vehicle.vehicleId] || [];
                          const modelAccessories = accessoriesByModel[vehicle.modelId] || [];
                          let accessoriesTotal = 0;
                          
                          return (
                            <div className="summary-accessories-list">
                              {vehicleAccessories.map(accessoryId => {
                                let accessory = modelAccessories.find(a => a.id === accessoryId);
                                if (!accessory) {
                                  accessory = accessories.find(a => a.id === accessoryId);
                                }
                                
                                if (!accessory) return null;
                                
                                const accessoryPrice = calculateAccessoryPrice(accessory);
                                accessoriesTotal += accessoryPrice;
                                
                                return (
                                  <div key={accessoryId} className="summary-accessory-item">
                                    <div className="summary-accessory-info">
                                      <Package size={14} />
                                      <span className="summary-accessory-name">{accessory.name}</span>
                                      <span className="summary-accessory-type">
                                        {accessory.accessoryPriceType === 'FIXED' 
                                          ? `(${t.fixedPrice || 'Fijo'})`
                                          : `(${t.dailyPrice || 'Por día'})`
                                        }
                                      </span>
                                    </div>
                                    <span className="summary-accessory-price">{formatPrice(accessoryPrice)}</span>
                                  </div>
                                );
                              })}
                              {accessoriesTotal > 0 && (
                                <div className="summary-accessories-subtotal">
                                  <span>{t.accessoriesSubtotal || 'Subtotal accesorios'}:</span>
                                  <span>{formatPrice(accessoriesTotal)}</span>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>

                {/* Desglose de precios (con base e IVA por línea para coincidir con Quipu) */}
                <div className="summary-section">
                  <h5 className="summary-section-title">
                    <Receipt size={18} />
                    {t.priceBreakdown || 'Desglose de precios'}
                  </h5>
                  
                  {(() => {
                    const durationInDays = calculateDuration();
                    let vehiclesTotal = 0;
                    let accessoriesOnlyTotal = 0;
                    let insuranceTotal = 0;
                    
                    formData.selectedVehicles.forEach(vehicle => {
                      const vehicleModel = availableVehicles.find(v => v.id === vehicle.modelId);
                      if (vehicleModel) {
                        vehiclesTotal += calculateVehiclePrice(vehicleModel, durationInDays);
                      }
                    });
                    
                    Object.entries(formData.selectedAccessories).forEach(([vehicleId, accessoryIds]) => {
                      const selectedVehicle = formData.selectedVehicles.find(v => v.vehicleId === parseInt(vehicleId));
                      if (selectedVehicle) {
                        const modelAccessories = accessoriesByModel[selectedVehicle.modelId] || [];
                        accessoryIds.forEach(accessoryId => {
                          let accessory = modelAccessories.find(a => a.id === accessoryId);
                          if (!accessory) accessory = accessories.find(a => a.id === accessoryId);
                          if (accessory) {
                            const price = calculateAccessoryPrice(accessory);
                            if (accessory.category === 'INSURANCE') insuranceTotal += price;
                            else accessoriesOnlyTotal += price;
                          }
                        });
                      }
                    });
                    
                    const deliveryCost = (formData.deliveryType === 'DELIVERY' && formData.deliveryCost) ? formData.deliveryCost : 0;
                    const rawTotal = Math.round((vehiclesTotal + accessoriesOnlyTotal + insuranceTotal + deliveryCost) * 100) / 100;
                    const lineAmounts = [
                      ...(vehiclesTotal > 0 ? [{ concept: 'vehicles', amount: vehiclesTotal }] : []),
                      ...(insuranceTotal > 0 ? [{ concept: 'insurance', amount: insuranceTotal }] : []),
                      ...(accessoriesOnlyTotal > 0 ? [{ concept: 'accessories', amount: accessoriesOnlyTotal }] : []),
                      ...(deliveryCost > 0 ? [{ concept: 'delivery', amount: deliveryCost }] : [])
                    ];
                    const linesWithVat = splitLineAmountsIntoBaseAndVat(lineAmounts, rawTotal);
                    const grandTotal = linesWithVat.reduce((s, l) => s + l.total, 0);
                    const manualPctSummary = (() => {
                      const raw = formData.manualDiscountPercent;
                      if (raw === '' || raw == null) return 0;
                      const n = parseFloat(String(raw).replace(',', '.'));
                      if (!Number.isFinite(n) || n <= 0) return 0;
                      return Math.min(100, n);
                    })();
                    const manualDiscountEurSummary =
                      Math.round(grandTotal * (manualPctSummary / 100) * 100) / 100;
                    const finalGrand =
                      Math.round((grandTotal - manualDiscountEurSummary) * 100) / 100;
                    const pctForPartial = (() => {
                      const raw = formData.partialDepositPercentOverride;
                      if (raw === '' || raw === null || raw === undefined) return settingsPartialPercent;
                      const n = parseInt(raw, 10);
                      if (!Number.isFinite(n) || n < 1 || n > 100) return settingsPartialPercent;
                      return n;
                    })();
                    const showPartialBreakdown =
                      selectedBusinessGroup === 'BIKES' &&
                      formData.paymentMode === 'PARTIAL' &&
                      formData.paymentMethod !== 'COLABORADOR' &&
                      user?.role !== 'AGENCY' &&
                      finalGrand > MIN_PARTIAL_TOTAL_EUR;
                    const depositEur = showPartialBreakdown
                      ? computeInitialDepositEuros(finalGrand, pctForPartial)
                      : 0;
                    const firstPaymentPartial = showPartialBreakdown
                      ? Math.min(depositEur, finalGrand)
                      : 0;
                    const remainderAfterDeposit = showPartialBreakdown
                      ? Math.round((finalGrand - firstPaymentPartial) * 100) / 100
                      : 0;
                    const vehiclesLine = linesWithVat.find(l => l.concept === 'vehicles');
                    const insuranceLine = linesWithVat.find(l => l.concept === 'insurance');
                    const accessoriesLine = linesWithVat.find(l => l.concept === 'accessories');
                    const deliveryLine = linesWithVat.find(l => l.concept === 'delivery');
                    
                    return (
                      <>
                        {vehiclesLine && (
                          <div className="summary-price-item">
                            <div>
                              <span>
                                {t.vehicles} (
                                {selectedBusinessGroup === 'CARRITOS'
                                  ? formatDuration()
                                  : `${durationInDays} ${durationInDays === 1 ? t.day || 'día' : t.days || 'días'}`}
                                ):
                              </span>
                              <div className="summary-price-subline text-muted small">
                                {t.base ?? 'Base'}: {formatPrice(vehiclesLine.base)} · {t.vat ?? 'IVA (21%)'}: {formatPrice(vehiclesLine.vat)}
                              </div>
                            </div>
                            <span>{formatPrice(vehiclesLine.total)}</span>
                          </div>
                        )}
                        {accessoriesLine && (
                          <div className="summary-price-item">
                            <div>
                              <span>
                                {t.accessories} (
                                {selectedBusinessGroup === 'CARRITOS'
                                  ? formatDuration()
                                  : `${durationInDays} ${durationInDays === 1 ? t.day || 'día' : t.days || 'días'}`}
                                ):
                              </span>
                              <div className="summary-price-subline text-muted small">
                                {t.base ?? 'Base'}: {formatPrice(accessoriesLine.base)} · {t.vat ?? 'IVA (21%)'}: {formatPrice(accessoriesLine.vat)}
                              </div>
                            </div>
                            <span>{formatPrice(accessoriesLine.total)}</span>
                          </div>
                        )}
                        {insuranceLine && (
                          <div className="summary-price-item">
                            <div>
                              <span>{t.insuranceSubtotal ?? 'Seguros'}:</span>
                              <div className="summary-price-subline text-muted small">
                                {t.base ?? 'Base'}: {formatPrice(insuranceLine.base)} · {t.vat ?? 'IVA (21%)'}: {formatPrice(insuranceLine.vat)}
                              </div>
                            </div>
                            <span>{formatPrice(insuranceLine.total)}</span>
                          </div>
                        )}
                        {deliveryLine && (
                          <div className="summary-price-item">
                            <div>
                              <span><MapPin size={14} /> {t.delivery || 'Entrega a domicilio'}:</span>
                              <div className="summary-price-subline text-muted small">
                                {t.base ?? 'Base'}: {formatPrice(deliveryLine.base)} · {t.vat ?? 'IVA (21%)'}: {formatPrice(deliveryLine.vat)}
                              </div>
                            </div>
                            <span>{formatPrice(deliveryLine.total)}</span>
                          </div>
                        )}
                        {manualDiscountEurSummary > 0.001 && (
                          <div
                            className="summary-price-item"
                            style={{
                              marginTop: '8px',
                              paddingTop: '8px',
                              borderTop: '1px dashed #d1d5db',
                              color: '#4b5563',
                              fontWeight: 500,
                            }}
                          >
                            <span>{t.manualDiscountPreviewSubtotal || 'Total sin descuento'}</span>
                            <span>{formatPrice(grandTotal)}</span>
                          </div>
                        )}
                        {manualDiscountEurSummary > 0.001 && (
                          <div className="summary-price-item" style={{ color: '#059669' }}>
                            <span>
                              {t.manualDiscountSummaryLabel || 'Descuento manual'} ({manualPctSummary}%)
                            </span>
                            <span>−{formatPrice(manualDiscountEurSummary)}</span>
                          </div>
                        )}
                        {/* Total = subtotal líneas menos descuento manual (si hay) */}
                        <div className="summary-total-section" style={{ marginTop: '8px' }}>
                          <div className="summary-item total">
                            <div className="summary-total-label-group">
                              <span className="summary-total-label">
                                {manualDiscountEurSummary > 0.001
                                  ? `${t.manualDiscountPreviewFinal || 'Total con descuento'}:`
                                  : `${t.total}:`}
                              </span>
                              <span className="summary-vat-included">{t.vatIncluded || 'IVA incluido'}</span>
                            </div>
                            <span className="summary-total-value">{formatPrice(finalGrand)}</span>
                          </div>
                        </div>
                        {showPartialBreakdown && (
                          <div className="summary-partial-box">
                            <div className="summary-partial-title">Desglose cobro fraccionado</div>
                            <div className="summary-price-item summary-partial-row">
                              <span>
                                Primer cobro (anticipo, {pctForPartial}%)
                              </span>
                              <span className="summary-partial-amount">{formatPrice(firstPaymentPartial)}</span>
                            </div>
                            <div className="summary-price-item summary-partial-row">
                              <span>Pendiente (liquidación posterior)</span>
                              <span className="summary-partial-amount muted">{formatPrice(remainderAfterDeposit)}</span>
                            </div>
                            <p className="summary-partial-note">
                              El total de la reserva arriba es el importe completo. Al cambiar el % solo cambia cómo se reparte entre anticipo y pendiente (€ enteros).
                            </p>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Total final (si el desglose anterior no incluyó ninguna línea, mostrar calculateTotal()) */}
                {(!formData.selectedVehicles?.length || formData.selectedVehicles.length === 0) && (
                  <div className="summary-total-section">
                    <div className="summary-item total">
                      <div className="summary-total-label-group">
                        <span className="summary-total-label">{t.total}:</span>
                        <span className="summary-vat-included">{t.vatIncluded || 'IVA incluido'}</span>
                      </div>
                      <span className="summary-total-value">{formatPrice(calculateTotal())}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Checkbox de aceptación de políticas de cancelación */}
              <div className="form-group" style={{ marginTop: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                  <input
                    type="checkbox"
                    checked={acceptedCancellationPolicy}
                    onChange={(e) => setAcceptedCancellationPolicy(e.target.checked)}
                    style={{ marginTop: '4px', width: '18px', height: '18px', cursor: 'pointer', accentColor: '#007bff' }}
                  />
                  <span style={{ flex: 1, color: '#374151', fontSize: '14px', lineHeight: '1.5' }}>
                    {t.cancellationPolicyAccept || 'Acepto las'}{' '}
                    <a
                      href={t.cancellationPolicyUrl || 'https://example.com/politicas-cancelacion'}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#007bff', textDecoration: 'underline', fontWeight: '600' }}
                    >
                      {t.cancellationPolicyLink || 'políticas de cancelación'}
                    </a>
                  </span>
                </label>
                {!acceptedCancellationPolicy && step === 5 && (
                  <div className="error-message" style={{ marginTop: '8px', marginLeft: '30px' }}>
                    {t.cancellationPolicyRequired || 'Debes aceptar las políticas de cancelación para continuar'}
                  </div>
                )}
              </div>
            </div>
          )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <div className="action-buttons">
            {reservationCreated ? (
              <button
                type="button"
                className="btn-primary"
                onClick={onClose}
              >
                <List size={18} />
                {t.backToReservations}
              </button>
            ) : (
              <>
            {step > 1 && (
              <button 
                className="btn-secondary" 
                onClick={() => {
                  if (selectedBusinessGroup === 'CARRITOS' && step === 4) {
                    setStep(2);
                  } else {
                    setStep(step - 1);
                  }
                }}
              >
                {t.previous}
              </button>
            )}
            
            {step < 5 ? (
              <button 
                className="btn-primary" 
                onClick={() => {
                  if (selectedBusinessGroup === 'CARRITOS' && step === 2) {
                    setStep(4);
                  } else {
                    setStep(step + 1);
                  }
                }}
                disabled={!isFormValid()}
              >
                {t.next}
              </button>
            ) : (
              <button 
                className="btn-primary" 
                onClick={handleCreateBooking}
                disabled={!isFormValid() || loading}
              >
                {loading ? <Loader size={16} /> : <Save size={16} />}
                {loading ? t.creating : t.createBookingBtn}
              </button>
            )}
              </>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export default CrearReserva; 